// code-guardian-ignore-duplicates: shared structural skeleton / cross-app type mirror — intentional pattern duplication (scheduler lifecycle, CRS-gateway wrappers, web/api type mirrors with no shared package), not copy-pasted logic; consolidating (base class / shared types pkg) is a separate refactor
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Edit3, Trash2, Loader2, AlertCircle, Check, X } from 'lucide-react';
import Modal from './Modal';
import { targetMetricsAPI, type SalesFeedFigure, type SubMetricSourceType } from '../services/api';
import { formatValidRange } from '../utils/validRange';
import { BlockLoader } from './loading';
import {
  SalesTargetsProductsField, loadSalesProductOptions, noSalesProductsPicked, salesFeedInputs, sameSalesProducts,
  type SalesProductOption,
} from './SalesTargetsProductsField';

export interface TargetMetric {
  id: number;
  project_id: number;
  metric_name: string;
  unit: string | null;
  higher_is_better: boolean;
  status: string;
  // Legacy single-flag (`is_scorecard_metric`) was removed from the modal in
  // Phase 3 — every project's Employee Score now supports including any
  // number of target metrics via the Configure tab. The column still exists
  // server-side for historical reads but is no longer written from the UI.
  target_count?: number;
  created_at?: string;
  // Legacy single source (target-level). Retained for back-compat reads on
  // the list row but no longer written by this modal — each sub-metric now
  // owns its own source. Backfilled metrics carry these for one release as
  // a fallback path inside the score engine.
  source_task_question_id?: string | null;
  source_aggregation?: 'sum' | 'avg' | 'latest' | null;
  // Combined formula over sub-metric codes. Backfilled to
  // "achievement / target * 100" on every existing metric so legacy scoring
  // is preserved. Null when the admin hasn't configured anything yet.
  formula?: string | null;
  /** Cohort definition consumed by cohort_*() formula functions.
   *  Mirrors the server-side `CohortGrouping` type in
   *  apps/api/src/utils/cohortGrouping.ts. */
  cohort_grouping?: CohortGrouping | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cohort grouping (client-side mirror of server types)
// ─────────────────────────────────────────────────────────────────────────────
//
// Stays in lock-step with apps/api/src/utils/cohortGrouping.ts. The
// picker below assembles values matching these shapes; the server
// re-validates on save so a stale client never persists garbage.

interface AonBucket {
  name: string;
  low_months: number;
  /** null = unbounded on the last bucket only. */
  high_months: number | null;
}
interface AonBucketsGrouping {
  kind: 'aon_buckets';
  honeymoon_months?: number;
  buckets: AonBucket[];
}
type SingleCohortGrouping =
  | { kind: 'project' }
  | AonBucketsGrouping
  | { kind: 'role' }
  | { kind: 'store_category' };

type CohortGrouping =
  | SingleCohortGrouping
  | { kind: 'composite'; groups: SingleCohortGrouping[] };

/** "What the engine does when no cohort_grouping is stored" — every
 *  employee in one project-wide bucket. Both the picker's initial
 *  state and the engine fallback agree on this. */
const DEFAULT_COHORT: CohortGrouping = { kind: 'project' };

/** Single-line summary used in collapsed views (e.g. metric list cell
 *  on Configure). Plain enough for an admin to skim. */
function summariseCohort(g: CohortGrouping | null | undefined): string {
  if (!g) return 'Project-wide (default)';
  if (g.kind === 'composite') {
    return g.groups.map((sub) => summariseCohort(sub)).join(' × ');
  }
  switch (g.kind) {
    case 'project':        return 'Project-wide';
    case 'role':           return 'By role';
    case 'store_category': return 'By store category';
    case 'aon_buckets': {
      const names = g.buckets.map((b) => b.name).join(', ');
      const honeymoon = g.honeymoon_months ? ` (honeymoon < ${g.honeymoon_months}m)` : '';
      return `By AON: ${names}${honeymoon}`;
    }
  }
}

/** Sensible starter buckets — matches what the seed-score editor uses
 *  on Configure so admins coming from there see something familiar. */
const SEED_AON_BUCKETS: AonBucket[] = [
  { name: '3-6m',  low_months: 3,  high_months: 6 },
  { name: '6-9m',  low_months: 6,  high_months: 9 },
  { name: '9-12m', low_months: 9,  high_months: 12 },
  { name: '1-2y',  low_months: 12, high_months: 24 },
  { name: '2y+',   low_months: 24, high_months: null },
];

// SubMetric shape is owned by DetailScreen's draft model now; the top-level
// component doesn't need a separate type. EligibleSourceQuestion stays
// exported-by-co-location since multiple sub-components consume it.

interface EligibleSourceQuestion {
  id: string;
  question_text: string;
  question_type: string;
  task_id: string;
  task_title: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  onMetricsChanged?: () => void;
  /** What the modal should show:
   *    'new'   → directly land on the New metric detail screen.
   *    number  → land on Edit detail screen for that target_metrics.id.
   *  Defaults to 'new' so the typical "+ Add Metric" CTA needs no
   *  extra plumbing. The legacy list-of-metrics view was retired —
   *  editing is now triggered per-row from the Configure tab. */
  target?: 'new' | number;
  /** Month (YYYY-MM-01) an edit applies FROM — the Configure tab passes its
   *  "Editing config that applies from" month, a per-metric Manage tab its
   *  selected month. Sub-metrics, formula and cohort are versioned by month;
   *  earlier months keep their own. Ignored for 'new'. */
  effectiveMonth?: string;
}

// Metric basics state lives inside DetailScreen now (one input + one
// checkbox). No top-level form-state type needed.

// ─────────────────────────────────────────────────────────────────────────────
// Top-level component
// ─────────────────────────────────────────────────────────────────────────────
//
// The modal is single-screen now: it lands directly on the New-metric
// editor (or the Edit editor for an existing metric, when the caller
// passes a `target` id). The legacy "list of custom metrics" view was
// retired — editing is now triggered per-row from the Configure tab via
// an Edit pencil that opens this modal pre-positioned to the metric.
//
// Detail screen layout: name, source picker, sub-metric
// list + formula builder, footer with Cancel + Save.

export default function ManageMetricsModal({ isOpen, onClose, projectId, onMetricsChanged, target = 'new', effectiveMonth }: Props) {
  const [metrics, setMetrics] = useState<TargetMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Eligible source questions are project-scoped; load once on open.
  const [eligibleQuestions, setEligibleQuestions] = useState<EligibleSourceQuestion[]>([]);
  // The project's Sales & Targets products, also once on open. Empty = the project
  // has none (or the read failed), and the Sales & Targets choices stay unavailable.
  const [salesProducts, setSalesProducts] = useState<SalesProductOption[]>([]);

  const fetchMetrics = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await targetMetricsAPI.getAll(projectId);
      if (res.data.status === 'success') {
        setMetrics(res.data.data || []);
      } else {
        setError(res.data.message || 'Failed to load metrics');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen) {
      fetchMetrics();
      (async () => {
        try {
          const res = await targetMetricsAPI.getEligibleSourceQuestions(projectId);
          if (res.data?.status === 'success') {
            setEligibleQuestions(res.data.data || []);
          } else {
            setEligibleQuestions([]);
          }
        } catch {
          setEligibleQuestions([]);
        }
      })();
      // Never rejects: a failed read answers an empty list.
      loadSalesProductOptions(projectId).then(setSalesProducts);
    }
  }, [isOpen, fetchMetrics, projectId]);

  // (Delete handler retired with the list view — Configure-tab row's
  // trash icon now drives delete.)

  // Render. `target` drives which detail we open with:
  //   'new'   → blank New-metric editor
  //   number  → Edit screen for that metric id
  // The metric prop is looked up from the freshly-fetched list so a
  // parent that opens the modal mid-fetch isn't stuck without basics.
  const targetIsExisting = typeof target === 'number';
  const activeMetric = targetIsExisting
    ? metrics.find((m) => m.id === target) ?? null
    : null;
  const titleLabel = targetIsExisting
    ? (activeMetric ? `Edit metric · ${activeMetric.metric_name}` : 'Edit metric')
    : 'Add metric';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={titleLabel}
      size="lg"
      // Modal-level footer removed — the × in the header is the universal
      // dismiss control, and DetailScreen renders its own action bar
      // (Cancel / Save).
      footer={undefined}
    >
      {error && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500 mt-0.5" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* While we're fetching the metric list for an Edit open, show a
          spinner so the DetailScreen doesn't render with a stale or
          missing `metric` prop. New flow doesn't need this. */}
      {loading && targetIsExisting && activeMetric === null ? (
        <BlockLoader className="py-12" />
      ) : (
        <DetailScreen
          // Remount per (metric, month) so reopening never shows a previous draft.
          key={`${String(target)}:${effectiveMonth ?? ''}`}
          projectId={projectId}
          metricId={target}
          effectiveMonth={effectiveMonth}
          metric={activeMetric}
          eligibleQuestions={eligibleQuestions}
          salesProducts={salesProducts}
          onBack={onClose}
          onSaved={async () => {
            await fetchMetrics();
            onMetricsChanged?.();
            onClose();
          }}
          onCreated={async (_newId) => {
            await fetchMetrics();
            onMetricsChanged?.();
            onClose();
          }}
        />
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Detail screen — metric basics + sub-metrics + formula
// ─────────────────────────────────────────────────────────────────────────────
//
// The legacy list-of-metrics screen and its delete handler were removed
// when this modal moved to "open on detail directly". Edit and delete
// for existing metrics happen on the Configure-tab Custom-metric row
// (pencil + trash) instead.

// Calculation-version labels — the same helpers the Configure tab uses
// (EmployeeScorePage), so both version pickers read identically.
const SENTINEL_VERSION = '1900-01-01';
const fmtVersionMonth = (v: string): string =>
  v === SENTINEL_VERSION
    ? 'Initial'
    : new Date(`${v}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });
// Label for the month immediately BEFORE a version's start — the last month the
// previous version still covers (e.g. version 2026-07-01 → "Jun 2026").
const monthBeforeLabel = (v: string): string => {
  const d = new Date(`${v}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

/** The months a calculation scores, from its start month up to the next version's. */
const calcRangeLabel = (from: string, next: string | null): string => {
  if (next) return from === SENTINEL_VERSION ? `All months through ${monthBeforeLabel(next)}` : `${fmtVersionMonth(from)} – ${monthBeforeLabel(next)}`;
  return from === SENTINEL_VERSION ? 'All months' : `${fmtVersionMonth(from)} onwards`;
};
const firstOfThisMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

/** The server's resolution of which calculation version the editor is showing. */
interface CalcVersionInfo {
  effective_from: string;
  next_effective_from: string | null;
  versions: string[];
}

/** An input's source fields that only mean something for Sales & Targets. */
interface SalesFeedFields {
  source_type: SubMetricSourceType;
  source_sales_figure: SalesFeedFigure | null;
  source_sales_products: string[] | null;
}

// The server keeps an input's figure and products when it is switched away from
// Sales & Targets (so a switch back, or a rollback, loses nothing). They mean
// nothing on any other source, so the editor reads them as empty there.
const readSalesFigure = (sourceType: unknown, raw: unknown): SalesFeedFigure | null =>
  sourceType === 'sales_targets' && (raw === 'target' || raw === 'sales') ? raw : null;
const readSalesProducts = (sourceType: unknown, raw: unknown): string[] | null => {
  if (sourceType !== 'sales_targets' || raw == null) return null;
  let list: unknown = raw;
  // The column is JSON; tolerate it arriving as text.
  if (typeof raw === 'string') {
    try { list = JSON.parse(raw); } catch { return null; }
  }
  return Array.isArray(list) ? list.map(String) : null;
};

/**
 * True when a Target input and a Sold input from Sales & Targets count different
 * products: the percentage would then divide one set of products by another.
 * Allowed (a formula may mean it), but nearly always a slip of the tick list.
 */
const salesProductsMismatch = (subs: SalesFeedFields[]): boolean => {
  const feed = subs.filter((s) => s.source_type === 'sales_targets');
  const targets = feed.filter((s) => s.source_sales_figure === 'target');
  const sold = feed.filter((s) => s.source_sales_figure === 'sales');
  return targets.some((t) => sold.some((s) => !sameSalesProducts(t.source_sales_products, s.source_sales_products)));
};

const SALES_PRODUCTS_MISMATCH_TEXT = 'Target and Sold count different products.';

// Formula words the server refuses in a calculation that has a Sales & Targets input.
const SALES_FEED_BANNED_WORDS = ['cohort_median', 'cohort_stddev', 'cohort_count', 'cohort_size', 'submitted_this_period'];

interface DetailProps {
  projectId: number;
  metricId: number | 'new';
  metric: TargetMetric | null;
  eligibleQuestions: EligibleSourceQuestion[];
  /** The project's Sales & Targets products; empty = none, so that source is not offered. */
  salesProducts: SalesProductOption[];
  effectiveMonth?: string;
  onBack: () => void;
  onSaved: () => Promise<void>;
  onCreated: (newId: number) => Promise<void>;
}

// Client-side slugifier kept in lock-step with the server's. Used to
// pre-compute sub-metric codes during the new-metric flow so the admin
// can reference them in the formula textarea before anything has been
// POSTed. Lowercases + replaces non-[a-z0-9] with underscores, strips
// leading/trailing underscores.
const slugifyCodeClient = (name: string): string =>
  String(name).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 50);

function DetailScreen({
  projectId, metricId, metric, eligibleQuestions, salesProducts, effectiveMonth, onBack, onSaved, onCreated,
}: DetailProps) {
  const isNew = metricId === 'new';

  // ──────────────────────────────────────────────────────────────────────
  // Draft model
  // ──────────────────────────────────────────────────────────────────────
  //
  // Everything the admin sees is a local draft. Sub-metric add / edit /
  // delete, formula edits, basics edits — all mutate state in this
  // component. A single Save at the bottom diffs the draft against the
  // server snapshot and fires the minimum set of API calls in the right
  // order (delete subs → create / update subs → patch formula → done).
  //
  // Server-id sub-metrics keep their UUID. Newly-added ones get a
  // "local-N" id; on Save we POST them and the parent refetches.

  interface DraftSub {
    id: string;
    name: string;
    code: string;
    source_type: SubMetricSourceType;
    source_question_id: string | null;
    source_aggregation: 'sum' | 'avg' | 'latest' | null;
    // Optional store_employee_picker question whose answer names the rated
    // employee (e.g. AM rates iPro). null = attribute to the submitter.
    source_target_picker_question_id: string | null;
    // Sales & Targets inputs only (null on every other source): which figure the
    // input carries, and the products it counts (null = all products).
    source_sales_figure: SalesFeedFigure | null;
    source_sales_products: string[] | null;
    // Valid range — a value outside it is ignored when scoring, as if there
    // were no value (e.g. Seed Score 0–100). null = no limit.
    valid_min: number | null;
    valid_max: number | null;
  }

  const [draftName, setDraftName] = useState<string>(metric?.metric_name ?? '');
  // Every scorecard metric is scored "higher value is better", so the toggle was
  // removed from the UI; the value is held constant at true (backend default).
  const [draftHigher] = useState<boolean>(metric?.higher_is_better ?? true);
  const [draftFormula, setDraftFormula] = useState<string>(metric?.formula ?? '');
  const [draftSubs, setDraftSubs] = useState<DraftSub[]>([]);
  const [deletedSubIds, setDeletedSubIds] = useState<string[]>([]);
  const [draftCohort, setDraftCohort] = useState<CohortGrouping>(metric?.cohort_grouping ?? DEFAULT_COHORT);

  // Frozen server snapshot for diff-on-save.
  const [serverName, setServerName] = useState<string>(metric?.metric_name ?? '');
  const [serverHigher, setServerHigher] = useState<boolean>(metric?.higher_is_better ?? true);
  const [serverFormula, setServerFormula] = useState<string>(metric?.formula ?? '');
  const [serverSubs, setServerSubs] = useState<DraftSub[]>([]);
  const [serverCohort, setServerCohort] = useState<CohortGrouping>(metric?.cohort_grouping ?? DEFAULT_COHORT);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formulaErr, setFormulaErr] = useState<string | null>(null);
  // The server's refusal to start a Sales & Targets change in a month that already
  // has figures. It names the month to pick, so it is shown beside the month picker
  // rather than in the general error line.
  const [historyErr, setHistoryErr] = useState<string | null>(null);
  const historyErrRef = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    // Save is at the foot of a scrolling modal and the month picker near its top.
    if (historyErr) historyErrRef.current?.scrollIntoView?.({ block: 'center' });
  }, [historyErr]);

  // Which version of the calculation this edit targets (existing metrics only).
  // Same semantics as the Configure tab's "Editing config that applies from":
  // `calcMonth` is the month the saved calculation applies FROM — if no version
  // starts there yet, saving starts one (pre-filled from the version in effect)
  // and earlier months keep theirs. `calcVersion` is what the server resolved.
  const [calcMonth, setCalcMonth] = useState<string>(effectiveMonth || firstOfThisMonth());
  const [calcVersion, setCalcVersion] = useState<CalcVersionInfo | null>(null);

  // Inline editor state: null = no editor open; 'new' = adding; or a
  // sub-metric id = editing that one. We render exactly one editor at a
  // time to keep the UI focused.
  const [activeEditor, setActiveEditor] = useState<string | 'new' | null>(null);

  // Monotonic local-id counter for staged-but-unsaved sub-metrics.
  const [localSeq, setLocalSeq] = useState(0);

  // Simple value-source picker — visible during new-metric flow only
  // when no explicit sub-metrics are staged. On Save we turn this into
  // a single "value" sub-metric — or, for Sales & Targets, into the standard
  // Target and Sold inputs with their formula.
  const [simpleSource, setSimpleSource] = useState<SubMetricSourceType>('manual');
  // Products for the Sales & Targets source. null = all products.
  const [simpleProducts, setSimpleProducts] = useState<string[] | null>(null);
  // "Use Sales & Targets" on an existing metric: whether its products field is
  // open, and what is picked in it. Nothing changes until Apply.
  const [feedOpen, setFeedOpen] = useState(false);
  const [feedProducts, setFeedProducts] = useState<string[] | null>(null);
  // The card's own "From" month moves the editor to that month, which loads that
  // month's calculation afresh. Set just before such a move so the card comes back
  // open, with the products already picked, instead of closing as a load normally does.
  const reopenFeedRef = useRef(false);
  const [simpleQuestionId, setSimpleQuestionId] = useState<string>('');
  const [simpleAgg, setSimpleAgg] = useState<'sum' | 'avg' | 'latest'>('sum');
  // Optional rated-employee picker for the simple source. '' = score the
  // submitter (legacy). Otherwise a store_employee_picker question whose
  // answer names the rated employee (e.g. an AM rating an iPro).
  const [simplePickerQuestionId, setSimplePickerQuestionId] = useState<string>('');

  // The eligible-questions endpoint returns BOTH numeric value questions and
  // store_employee_picker questions; split them so the value dropdown never
  // lists a picker and the picker dropdown never lists a value question.
  const valueQuestions = eligibleQuestions.filter((q) => q.question_type !== 'store_employee_picker');
  const pickerQuestions = eligibleQuestions.filter((q) => q.question_type === 'store_employee_picker');
  // Only offer pickers from the SAME task as the chosen value question —
  // pairing happens within one submission, so a cross-task picker can't match.
  const simpleValueTaskId = valueQuestions.find((q) => q.id === simpleQuestionId)?.task_id ?? null;
  const simplePickerQuestions = simpleValueTaskId
    ? pickerQuestions.filter((q) => q.task_id === simpleValueTaskId)
    : pickerQuestions;

  useEffect(() => {
    if (!simpleQuestionId && valueQuestions.length > 0) {
      setSimpleQuestionId(valueQuestions[0].id);
    }
  }, [valueQuestions, simpleQuestionId]);

  // Load existing metric's sub-metrics + formula into draft + server snapshots.
  const load = useCallback(async () => {
    if (isNew || typeof metricId !== 'number') return;
    setLoading(true);
    try {
      const res = await targetMetricsAPI.listSubMetrics(metricId, calcMonth);
      if (res.data.status === 'success') {
        // A month switch starts a fresh draft of THAT month's calculation.
        setCalcVersion(res.data.data.version ?? null);
        setDeletedSubIds([]);
        setActiveEditor(null);
        setFeedOpen(reopenFeedRef.current);
        reopenFeedRef.current = false;
        setHistoryErr(null);
        const subs: DraftSub[] = (res.data.data.sub_metrics || []).map((s: any) => {
          // A Sales & Targets input has no valid range (a manager's figure is a
          // team total, far outside any range sized for one person).
          const fromSales = s.source_type === 'sales_targets';
          return {
            id: String(s.id),
            name: String(s.name),
            code: String(s.code),
            source_type: s.source_type,
            source_question_id: s.source_question_id ?? null,
            source_aggregation: s.source_aggregation ?? null,
            source_target_picker_question_id: s.source_target_picker_question_id ?? null,
            source_sales_figure: readSalesFigure(s.source_type, s.source_sales_figure),
            source_sales_products: readSalesProducts(s.source_type, s.source_sales_products),
            valid_min: fromSales || s.valid_min == null ? null : Number(s.valid_min),
            valid_max: fromSales || s.valid_max == null ? null : Number(s.valid_max),
          };
        });
        setDraftSubs(subs);
        setServerSubs(subs);
        const f = res.data.data.metric?.formula ?? '';
        setDraftFormula(f);
        setServerFormula(f);
        // cohort_grouping is optional on the response — older rows
        // pre-migration return undefined and we keep the default.
        const cg: CohortGrouping = res.data.data.metric?.cohort_grouping ?? DEFAULT_COHORT;
        setDraftCohort(cg);
        setServerCohort(cg);
      }
    } catch {
      // Non-fatal; admin sees an inline error on the next interaction.
    } finally {
      setLoading(false);
    }
  }, [metricId, isNew, calcMonth]);
  useEffect(() => { load(); }, [load]);

  // Live formula validation against the staged sub-metric codes.
  useEffect(() => {
    // The two rules the server adds once an input comes from Sales & Targets,
    // checked here first: for a new metric a refusal arrives only after the metric
    // itself has been created, and a retry then finds its name taken.
    const fromSales = draftSubs.some((s) => s.source_type === 'sales_targets');
    if (!draftFormula.trim()) {
      setFormulaErr(fromSales ? 'A parameter from Sales & Targets needs a formula.' : null);
      return;
    }
    // A sub-metric deleted here that the formula still uses. This is why Save stays
    // off after a delete, so it is said in the words on screen, not the parser's.
    const known = new Set(draftSubs.map((s) => s.code.toLowerCase()));
    const missing = [...new Set(tokenize(draftFormula).flatMap((t) => (t.kind === 'ref' && !known.has(t.code) ? [t.code] : [])))];
    if (missing.length > 0) {
      const names = missing.map((code) => serverSubs.find((s) => s.code.toLowerCase() === code)?.name ?? code);
      setFormulaErr(names.length === 1
        ? `The formula uses ${names[0]}, which is not a sub-metric. Take it out of the formula.`
        : `The formula uses ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}, which are not sub-metrics. Take them out of the formula.`);
      return;
    }
    try {
      validateFormulaClient(draftFormula, draftSubs.map((s) => s.code));
      // These compare people with their peers, and such an input holds managers'
      // team totals beside individual figures, so there is no peer group to use.
      const banned = fromSales
        ? SALES_FEED_BANNED_WORDS.find((w) => new RegExp(`\\b${w}\\b`, 'i').test(draftFormula))
        : undefined;
      setFormulaErr(banned ? `A parameter from Sales & Targets cannot use ${banned}.` : null);
    } catch (e: any) {
      setFormulaErr(e.message);
    }
  }, [draftFormula, draftSubs, serverSubs]);

  // ──────────────────────────────────────────────────────────────────────
  // Sub-metric editing — all changes stay in draft state.
  // ──────────────────────────────────────────────────────────────────────

  const commitNewSub = (data: SubMetricEditorOutput) => {
    const seq = localSeq + 1;
    setLocalSeq(seq);
    const code = slugifyCodeClient(data.name);
    if (draftSubs.some((s) => s.code === code)) {
      alert(`A sub-metric with code "${code}" already exists. Pick a different name.`);
      return;
    }
    setDraftSubs((prev) => [
      ...prev,
      {
        id: `local-${seq}`,
        name: data.name,
        code,
        source_type: data.source_type,
        source_question_id: data.source_question_id,
        source_aggregation: data.source_aggregation,
        source_target_picker_question_id: data.source_target_picker_question_id,
        source_sales_figure: data.source_sales_figure,
        source_sales_products: data.source_sales_products,
        valid_min: data.valid_min,
        valid_max: data.valid_max,
      },
    ]);
    setActiveEditor(null);
  };

  const commitEditSub = (id: string, data: SubMetricEditorOutput) => {
    const newCode = slugifyCodeClient(data.name);
    if (draftSubs.some((s) => s.id !== id && s.code === newCode)) {
      alert(`A sub-metric with code "${newCode}" already exists. Pick a different name.`);
      return;
    }
    setDraftSubs((prev) => prev.map((s) => s.id === id
      ? { ...s, name: data.name, code: newCode, source_type: data.source_type, source_question_id: data.source_question_id, source_aggregation: data.source_aggregation, source_target_picker_question_id: data.source_target_picker_question_id, source_sales_figure: data.source_sales_figure, source_sales_products: data.source_sales_products, valid_min: data.valid_min, valid_max: data.valid_max }
      : s,
    ));
    setActiveEditor(null);
  };

  // "Use Sales & Targets" on an existing metric: one decision (the products)
  // replaces the whole draft with the standard set-up. Done input by input in the
  // editors below it is a dozen clicks, each a place to slip: the same figure
  // picked twice, products ticked on one input only, a rename that breaks the formula.
  const openSalesFeed = () => {
    // Start from the products the draft already counts, when its inputs agree.
    const feed = draftSubs.filter((s) => s.source_type === 'sales_targets');
    const agreed = feed.length > 0 && feed.every((s) => sameSalesProducts(s.source_sales_products, feed[0].source_sales_products));
    setFeedProducts(agreed ? feed[0].source_sales_products : null);
    setFeedOpen(true);
  };

  const applySalesFeed = () => {
    if (noSalesProductsPicked(feedProducts)) return;
    const feed = salesFeedInputs(feedProducts);
    let seq = localSeq;
    const next: DraftSub[] = feed.sub_metrics.map((leg) => {
      // An input that already has this code keeps its row. Sent as a new row, the
      // server would drop the old one first, and the figures stored against it go
      // with it even when nothing about the input changed.
      const same = draftSubs.find((s) => s.code === leg.code);
      return {
        id: same ? same.id : `local-${++seq}`,
        name: leg.name,
        code: leg.code,
        source_type: leg.source_type,
        source_question_id: null,
        source_aggregation: null,
        source_target_picker_question_id: null,
        source_sales_figure: leg.source_sales_figure,
        source_sales_products: leg.source_sales_products,
        valid_min: null,
        valid_max: null,
      };
    });
    setLocalSeq(seq);
    const kept = new Set(next.map((s) => s.id));
    const dropped = draftSubs.filter((s) => !kept.has(s.id) && !s.id.startsWith('local-')).map((s) => s.id);
    if (dropped.length > 0) setDeletedSubIds((prev) => [...prev, ...dropped]);
    setDraftSubs(next);
    setDraftFormula(feed.formula);
    setActiveEditor(null);
    setFeedOpen(false);
  };

  const deleteSub = (sub: DraftSub) => {
    const lines = [`Delete sub-metric "${sub.name}"?`];
    // What Save does next, said before the click: a saved input takes its uploaded
    // figures for this calculation's months with it (the server's delete cascades).
    if (calcVersion && !sub.id.startsWith('local-') && sub.source_type === 'manual') {
      const next = [...calcVersion.versions].sort().find((v) => v > calcMonth) ?? null;
      lines.push(`When you save, its uploaded figures for these months are removed and the months are scored again: ${calcRangeLabel(calcMonth, next)}.`);
    }
    if (tokenize(draftFormula).some((t) => t.kind === 'ref' && t.code === sub.code.toLowerCase())) {
      lines.push('The formula uses it, so take it out of the formula before you save.');
    }
    if (!window.confirm(lines.join('\n\n'))) return;
    // Local sub: just drop it. Server sub: drop and remember to DELETE
    // on Save.
    if (sub.id.startsWith('local-')) {
      setDraftSubs((prev) => prev.filter((s) => s.id !== sub.id));
    } else {
      setDraftSubs((prev) => prev.filter((s) => s.id !== sub.id));
      setDeletedSubIds((prev) => [...prev, sub.id]);
    }
    if (activeEditor === sub.id) setActiveEditor(null);
  };

  // ──────────────────────────────────────────────────────────────────────
  // Dirty / save eligibility
  // ──────────────────────────────────────────────────────────────────────

  const isDirty = useMemo(() => {
    if (isNew) return true;
    if (draftName !== serverName) return true;
    if (draftHigher !== serverHigher) return true;
    if ((draftFormula || '').trim() !== (serverFormula || '').trim()) return true;
    if (JSON.stringify(draftCohort) !== JSON.stringify(serverCohort)) return true;
    if (deletedSubIds.length > 0) return true;
    if (draftSubs.length !== serverSubs.length) return true;
    for (const s of draftSubs) {
      if (s.id.startsWith('local-')) return true;
      const orig = serverSubs.find((o) => o.id === s.id);
      if (!orig) return true;
      if (orig.name !== s.name || orig.code !== s.code
        || orig.source_type !== s.source_type
        || (orig.source_question_id ?? null) !== (s.source_question_id ?? null)
        || (orig.source_aggregation ?? null) !== (s.source_aggregation ?? null)
        || (orig.source_target_picker_question_id ?? null) !== (s.source_target_picker_question_id ?? null)
        || (orig.source_sales_figure ?? null) !== (s.source_sales_figure ?? null)
        // By content: the same products ticked in another order are not a change,
        // and a different list of the same length is.
        || !sameSalesProducts(orig.source_sales_products, s.source_sales_products)
        || orig.valid_min !== s.valid_min || orig.valid_max !== s.valid_max) return true;
    }
    return false;
  }, [isNew, draftName, draftHigher, draftFormula, draftCohort, draftSubs, deletedSubIds, serverName, serverHigher, serverFormula, serverCohort, serverSubs]);

  // Moving the editor to another month starts a fresh draft of that month's
  // calculation. Asked from the Use Sales & Targets card, the card stays open so the
  // month is one of its two choices rather than a detour through the block above.
  const switchCalcMonth = (next: string, keepSalesCardOpen = false) => {
    if (next === calcMonth) return;
    if (isDirty && !window.confirm('Discard unsaved changes?')) return;
    reopenFeedRef.current = keepSalesCardOpen;
    setCalcMonth(next);
  };

  // The new-metric picker decides the calculation only while no sub-metric is staged.
  const simpleSalesFeed = isNew && draftSubs.length === 0 && simpleSource === 'sales_targets';
  // "Choose products" with nothing ticked would save an input that counts nothing.
  const noProductsPicked = simpleSalesFeed && noSalesProductsPicked(simpleProducts);

  const canSave = !!draftName.trim() && !formulaErr && !saving && !noProductsPicked && (isNew || isDirty);

  // ──────────────────────────────────────────────────────────────────────
  // Single Save — diffs draft vs server and fires API calls in the right
  // order. Errors surface inline and STOP — partial state is fine since
  // the next reload syncs everything.
  // ──────────────────────────────────────────────────────────────────────

  type DefinitionPayload = Parameters<typeof targetMetricsAPI.saveMetricDefinition>[1];

  // The draft's sub-metrics as one calculation. Every row names the two
  // Sales & Targets fields, null on any other source, so an input switched back
  // to Manual or Task question does not keep its figure and products.
  const definitionLegs = (): DefinitionPayload['sub_metrics'] => draftSubs.map((s) => ({
    id: s.id.startsWith('local-') ? null : s.id,
    name: s.name,
    code: s.code,
    source_type: s.source_type,
    source_question_id: s.source_question_id,
    source_aggregation: s.source_aggregation,
    source_target_picker_question_id: s.source_target_picker_question_id,
    source_sales_figure: s.source_sales_figure,
    source_sales_products: s.source_sales_products,
    valid_min: s.valid_min,
    valid_max: s.valid_max,
  }));

  // One definition save, plus the one question the server can ask back. Moving an
  // input to or from Sales & Targets in a month that already holds figures the
  // change may replace (this month's uploads, the latest closed month's stored
  // figures) is answered 409 SALES_FEED_CONFIRM with the sentence to show; saying
  // yes resends the same save with `confirm_replace`. false = the admin said no,
  // and nothing was saved.
  const saveDefinition = async (theMetricId: number, payload: DefinitionPayload): Promise<boolean> => {
    try {
      await targetMetricsAPI.saveMetricDefinition(theMetricId, payload);
      return true;
    } catch (err: any) {
      const refusal = err?.response?.status === 409 ? err.response.data : null;
      if (refusal?.code !== 'SALES_FEED_CONFIRM') throw err;
      if (!window.confirm(String(refusal.message || 'Existing figures will be replaced by Sales & Targets.'))) return false;
      await targetMetricsAPI.saveMetricDefinition(theMetricId, { ...payload, confirm_replace: true });
      return true;
    }
  };

  const save = async () => {
    setError(null);
    setHistoryErr(null);
    if (!draftName.trim()) { setError('Metric name is required'); return; }
    setSaving(true);
    try {
      let theMetricId: number;

      if (isNew) {
        const res = await targetMetricsAPI.create({
          project_id: projectId,
          metric_name: draftName.trim(),
          higher_is_better: draftHigher,
        });
        theMetricId = Number(res.data?.data?.id);
        if (!theMetricId) throw new Error('Server did not return a new metric id');
        // create() doesn't accept cohort_grouping (the controller's
        // POST handler still uses the legacy whitelist) — patch
        // immediately after creation when the admin changed it from
        // the default. Keeps the create endpoint contract narrow.
        if (JSON.stringify(draftCohort) !== JSON.stringify(DEFAULT_COHORT)) {
          await targetMetricsAPI.update(theMetricId, { cohort_grouping: draftCohort });
        }
      } else {
        theMetricId = metricId as number;
        // The metric's NAME is its identity (unique per project) — global, never
        // versioned — so it is patched on its own, and only when changed.
        if (draftName !== serverName || draftHigher !== serverHigher) {
          const payload: Record<string, unknown> = {};
          if (draftName !== serverName)       payload.metric_name = draftName.trim();
          if (draftHigher !== serverHigher)   payload.higher_is_better = draftHigher;
          await targetMetricsAPI.update(theMetricId, payload);
        }
        // Everything that decides HOW the value is calculated — the sub-metrics and
        // their sources, the formula, the cohort grouping — is saved together as ONE
        // version of the calculation, applying from `calcMonth`, in a single
        // transaction. If no version starts at that month yet the server starts one,
        // so every earlier month keeps the calculation it had.
        const saved = await saveDefinition(theMetricId, {
          effective_from: calcMonth,
          based_on: calcVersion?.effective_from ?? SENTINEL_VERSION,
          formula: (draftFormula || '').trim() || null,
          cohort_grouping: draftCohort,
          sub_metrics: definitionLegs(),
        });
        if (!saved) return;
      }

      // 3a. New metric with NO explicit sub-metrics → simple flow: auto-
      //     create one "value" sub-metric from the picker + formula="value".
      // 3b. New metric with staged sub-metrics → create them in display order.
      //     (A new metric has no history, so this is its base calculation.)
      // 3c. New metric from Sales & Targets (the picker's third card, or a staged
      //     input with that source) → ONE definition save on the base version:
      //     inputs and formula land together or not at all. Created call by call,
      //     a failure part-way leaves an input with no formula, which no scoring
      //     run can use and which holds the month of every role that carries it.
      if (simpleSalesFeed) {
        const feed = salesFeedInputs(simpleProducts);
        await targetMetricsAPI.saveMetricDefinition(theMetricId, {
          effective_from: SENTINEL_VERSION,
          based_on: SENTINEL_VERSION,
          formula: feed.formula,
          cohort_grouping: draftCohort,
          sub_metrics: feed.sub_metrics,
        });
      } else if (isNew && draftSubs.some((s) => s.source_type === 'sales_targets')) {
        await targetMetricsAPI.saveMetricDefinition(theMetricId, {
          effective_from: SENTINEL_VERSION,
          based_on: SENTINEL_VERSION,
          formula: (draftFormula || '').trim() || null,
          cohort_grouping: draftCohort,
          sub_metrics: definitionLegs(),
        });
      } else if (isNew && draftSubs.length === 0) {
        if (simpleSource === 'task_question' && !simpleQuestionId) {
          throw new Error('Pick a task question for the value source');
        }
        // Keep the picker only if it belongs to the chosen value question's task.
        const simpleValidPicker = simpleSource === 'task_question'
          && simplePickerQuestionId
          && simplePickerQuestions.some((q) => q.id === simplePickerQuestionId)
          ? simplePickerQuestionId
          : null;
        await targetMetricsAPI.createSubMetric(theMetricId, {
          name: 'Value',
          code: 'value',
          source_type: simpleSource,
          source_question_id: simpleSource === 'task_question' ? simpleQuestionId : null,
          source_aggregation: simpleSource === 'task_question' ? simpleAgg : null,
          source_target_picker_question_id: simpleValidPicker,
          display_order: 0,
        });
        await targetMetricsAPI.updateFormula(theMetricId, 'value');
      } else if (isNew) {
        for (let i = 0; i < draftSubs.length; i++) {
          const s = draftSubs[i];
          const isLocal = s.id.startsWith('local-');
          if (isLocal) {
            await targetMetricsAPI.createSubMetric(theMetricId, {  // codeguardian-ignore: serial by design (sequential dependency / ordered aggregation / rate-limited I/O)
              name: s.name,
              code: s.code,
              source_type: s.source_type,
              source_question_id: s.source_question_id,
              source_aggregation: s.source_aggregation,
              source_target_picker_question_id: s.source_target_picker_question_id,
              source_sales_figure: s.source_sales_figure,
              source_sales_products: s.source_sales_products,
              display_order: i,
              valid_min: s.valid_min,
              valid_max: s.valid_max,
            });
          } else {
            const orig = serverSubs.find((o) => o.id === s.id);
            const rangeChanged = !orig || orig.valid_min !== s.valid_min || orig.valid_max !== s.valid_max;
            const changed = !orig || orig.name !== s.name || orig.code !== s.code
              || orig.source_type !== s.source_type
              || (orig.source_question_id ?? null) !== (s.source_question_id ?? null)
              || (orig.source_aggregation ?? null) !== (s.source_aggregation ?? null)
              || (orig.source_target_picker_question_id ?? null) !== (s.source_target_picker_question_id ?? null)
              || (orig.source_sales_figure ?? null) !== (s.source_sales_figure ?? null)
              || !sameSalesProducts(orig.source_sales_products, s.source_sales_products)
              || rangeChanged;
            if (changed) {
              await targetMetricsAPI.updateSubMetric(theMetricId, s.id, {  // codeguardian-ignore: serial by design (sequential dependency / ordered aggregation / rate-limited I/O)
                name: s.name,
                code: s.code,
                source_type: s.source_type,
                source_question_id: s.source_question_id,
                source_aggregation: s.source_aggregation,
                source_target_picker_question_id: s.source_target_picker_question_id,
                source_sales_figure: s.source_sales_figure,
                source_sales_products: s.source_sales_products,
                display_order: i,
                // Only when it changed, so saving an untouched leg never writes the range.
                ...(rangeChanged ? { valid_min: s.valid_min, valid_max: s.valid_max } : {}),
              });
            }
          }
        }
        // 4. Patch formula if it changed (or set initially on create).
        const newFormula = (draftFormula || '').trim() || null;
        const oldFormula = (serverFormula || '').trim() || null;
        if (newFormula !== oldFormula) {
          await targetMetricsAPI.updateFormula(theMetricId, newFormula);
        }
      }

      // Refresh server-side snapshots to match what was just saved so
      // isDirty falls back to false and Save disables until the next edit.
      setServerName(draftName.trim());
      setServerHigher(draftHigher);
      setServerFormula((draftFormula || '').trim());
      setServerCohort(draftCohort);
      // Sub-metric snapshot is reseeded by the parent refetch + our load();
      // copying draftSubs here would leave local-N ids in the snapshot.

      if (isNew) {
        await onCreated(theMetricId);
      } else {
        await onSaved();
      }
    } catch (err: any) {
      const refusal = err?.response?.status === 409 ? err.response.data : null;
      // Only where the month picker is on screen to show it beside.
      if (!isNew && calcVersion && refusal?.code === 'SALES_FEED_HISTORY' && refusal.message) {
        setHistoryErr(String(refusal.message));
      } else {
        setError(err?.response?.data?.message || err?.message || 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) return;
    onBack();
  };

  // ──────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Breadcrumb removed — the modal title above (set by the parent
          to "Add metric" or "Edit metric · {name}") already labels the
          screen, and there's no list view to go back to. The Cancel
          button in the footer covers the discard-and-close intent. */}

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500 mt-0.5" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Metric basics — single row */}
      <section>
        <SectionLabel>Metric</SectionLabel>
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
          <input
            type="text"
            placeholder="Metric name (e.g. Sales)"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            className="min-w-0 flex-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
            autoFocus={isNew}
          />
        </div>
      </section>

      {/* Calculation version — existing metrics only. Mirrors the Configure tab's
          "Editing config that applies from" block: the sub-metrics, formula and
          cohort below are ONE version of this metric's calculation, applying from
          the picked month; earlier months keep their own (e.g. a value that is
          bulk-uploaded through June can be task-derived from July). A new metric
          has no history, so its first calculation applies to every month. */}
      {!isNew && calcVersion && (() => {
        const versionsAsc = [...calcVersion.versions].sort();
        const isExisting = versionsAsc.includes(calcMonth);
        // The version in effect at the picked month — what a NEW version is
        // pre-filled from, and what keeps covering the earlier months.
        const effectiveBefore = versionsAsc.filter((v) => v <= calcMonth).pop();
        // After "Figures exist up to <month>. Pick <next month> or later." the month is
        // changed here, beside that sentence. The refused draft's products go with the
        // move and the card comes back open, as it does from its own From picker.
        // Otherwise the card reopens on All products and the parameter is saved on
        // the wrong products with nothing on screen saying so.
        const switchMonth = (next: string) => {
          const feed = draftSubs.filter((s) => s.source_type === 'sales_targets');
          const carry = !!historyErr && feed.length > 0
            && feed.every((s) => sameSalesProducts(s.source_sales_products, feed[0].source_sales_products));
          if (carry) setFeedProducts(feed[0].source_sales_products);
          switchCalcMonth(next, carry);
        };
        return (
          <section>
            <SectionLabel>Calculation</SectionLabel>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <label className="font-medium text-gray-700">Editing calculation that applies from</label>
                <input
                  type="month"
                  value={calcMonth.slice(0, 7)}
                  onChange={(e) => e.target.value && switchMonth(`${e.target.value}-01`)}
                  className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                />
                <span className="text-xs text-gray-500">
                  onwards — earlier months keep their own calculation.
                </span>
              </div>
              {historyErr && (
                <p ref={historyErrRef} role="alert" className="text-xs font-medium text-red-600">{historyErr}</p>
              )}

              <div className="text-xs">
                {isExisting ? (
                  <span className="text-gray-600">
                    Editing the{' '}
                    <span className="font-semibold text-gray-800">{fmtVersionMonth(calcMonth)}</span>{' '}
                    version — saving updates only this version.
                  </span>
                ) : (
                  <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-800">
                    New version starting{' '}
                    <span className="font-semibold">{fmtVersionMonth(calcMonth)}</span>
                    {effectiveBefore && (
                      <> — pre-filled from the <span className="font-semibold">{fmtVersionMonth(effectiveBefore)}</span> calculation; earlier months stay on it.</>
                    )}{' '}
                    Saving creates it without touching earlier months.
                  </span>
                )}
              </div>

              {versionsAsc.length > 0 && (
                <div className="text-xs text-gray-600">
                  <div className="mb-1 font-medium text-gray-500">Which calculation scores which months</div>
                  <ul className="space-y-0.5">
                    {versionsAsc.map((v, i) => {
                      const range = calcRangeLabel(v, versionsAsc[i + 1] ?? null);
                      const isCurrent = v === calcMonth;
                      return (
                        <li key={v} className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => switchMonth(v)}
                            className={`rounded px-1.5 py-0.5 ${
                              isCurrent
                                ? 'bg-primary-600 text-white'
                                : 'bg-white text-blue-600 ring-1 ring-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {fmtVersionMonth(v)}
                          </button>
                          <span className="text-gray-500">→ {range}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* Simple value source — shown during create. When the admin has
          staged at least one sub-metric (or is currently adding one),
          the metric's value comes from the formula instead, so this
          picker is overridden and rendered disabled with an explainer
          line. Kept VISIBLE (rather than hidden) so the relationship
          stays clear at a glance. */}
      {isNew && (() => {
        const sourceOverridden = draftSubs.length > 0 || activeEditor === 'new';
        return (
          <section>
            <SectionLabel>How are values measured?</SectionLabel>
            <div
              className={`rounded-lg border border-gray-200 bg-white p-3 space-y-2 ${sourceOverridden ? 'opacity-50 pointer-events-none' : ''}`}
              aria-disabled={sourceOverridden || undefined}
            >
              {sourceOverridden ? (
                <p className="text-[11px] text-amber-700">
                  Sub-metrics added — the metric's value is derived from the formula below.
                  This picker is disabled.
                </p>
              ) : (
                <p className="text-[11px] text-gray-500">
                  Pick a source for this metric. For more complex calculations,
                  add explicit sub-metrics + a formula below instead.
                </p>
              )}
              {/* The source options on the same row to keep the picker
                  compact. Task-question dropdowns (or the Sales & Targets
                  products) drop to a second row below when that option is
                  active. */}
              <div className="grid grid-cols-3 gap-2">
                <RadioCard
                  checked={simpleSource === 'manual'}
                  onChange={() => setSimpleSource('manual')}
                  title="Manual upload"
                  description="Admins enter the value on the Manage tab each period."
                />
                <RadioCard
                  checked={simpleSource === 'task_question'}
                  onChange={() => setSimpleSource('task_question')}
                  disabled={eligibleQuestions.length === 0}
                  title="From task question"
                  description="Auto-aggregate numeric answers to a flagged task question."
                />
                {/* Nothing to count for a project with no Sales & Targets products. */}
                <RadioCard
                  checked={simpleSource === 'sales_targets'}
                  onChange={() => setSimpleSource('sales_targets')}
                  disabled={salesProducts.length === 0}
                  title="Sales & Targets"
                  description="Sold ÷ target, from Sales & Targets."
                />
              </div>
              {simpleSource === 'sales_targets' && (
                <SalesTargetsProductsField
                  options={salesProducts}
                  value={simpleProducts}
                  onChange={setSimpleProducts}
                />
              )}
              {simpleSource === 'task_question' && (
                <>
                  <div className="grid grid-cols-[1fr_120px] gap-2">
                    <select
                      value={simpleQuestionId}
                      onChange={(e) => setSimpleQuestionId(e.target.value)}
                      className="min-w-0 rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-primary-500 focus:outline-none"
                    >
                      <option value="">— pick a question —</option>
                      {valueQuestions.map((q) => (
                        <option key={q.id} value={q.id}>
                          {q.task_title} · {q.question_text.slice(0, 50)}{q.question_text.length > 50 ? '…' : ''}
                        </option>
                      ))}
                    </select>
                    <select
                      value={simpleAgg}
                      onChange={(e) => setSimpleAgg(e.target.value as 'sum' | 'avg' | 'latest')}
                      className="rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-primary-500 focus:outline-none"
                    >
                      <option value="sum">Sum</option>
                      <option value="avg">Average</option>
                      <option value="latest">Latest</option>
                    </select>
                  </div>
                  {/* Rated-employee picker — only shown when the chosen
                      question's task carries a store_employee_picker question.
                      Needed for feedback metrics (an AM rates an iPro) so the
                      value lands on the rated employee, not the submitter. */}
                  {simplePickerQuestions.length > 0 && (
                    <div className="space-y-1">
                      <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500">
                        Rated employee from
                      </label>
                      <select
                        value={simplePickerQuestionId}
                        onChange={(e) => setSimplePickerQuestionId(e.target.value)}
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-primary-500 focus:outline-none"
                      >
                        <option value="">Score the submitting employee (default)</option>
                        {simplePickerQuestions.map((q) => (
                          <option key={q.id} value={q.id}>
                            {q.task_title} · {q.question_text.slice(0, 50)}{q.question_text.length > 50 ? '…' : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-gray-400">
                        Attributes the value to the employee selected in this picker question — paired per submission.
                      </p>
                    </div>
                  )}
                </>
              )}
              {valueQuestions.length === 0 && simpleSource !== 'task_question' && (
                <div className="text-[11px] text-gray-400">
                  No eligible numeric task questions in this project yet.
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* Use Sales & Targets — existing metrics, in a project that has Sales &
          Targets products. The short path to the same set-up a new metric gets
          from the picker's third card; the per-input editors below stay for
          unusual formulas. The card carries the month it starts from (the same
          month as the "applies from" block above).
          Shown once the month's calculation has loaded (as that block is): an
          Apply made before then would be overwritten when the load lands. */}
      {!isNew && !loading && calcVersion && salesProducts.length > 0 && (
        <section>
          {feedOpen ? (
            <div className="rounded-lg border border-primary-300 bg-primary-50/40 p-3 space-y-2">
              <div className="text-sm font-medium text-gray-800">Use Sales &amp; Targets</div>
              <SalesTargetsProductsField
                options={salesProducts}
                value={feedProducts}
                onChange={setFeedProducts}
              />
              {/* The month this starts from, beside the products: the two choices
                  the card is for. It is the same month as the Calculation block
                  above, so changing either moves both. */}
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <label htmlFor="sales-feed-from" className="text-xs font-medium text-gray-600">From</label>
                <input
                  id="sales-feed-from"
                  type="month"
                  value={calcMonth.slice(0, 7)}
                  onChange={(e) => e.target.value && switchCalcMonth(`${e.target.value}-01`, true)}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setFeedOpen(false)}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={applySalesFeed}
                  disabled={noSalesProductsPicked(feedProducts)}
                  className="rounded-md bg-primary-600 px-3 py-1 text-xs font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={openSalesFeed}
              className="block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-left transition hover:bg-gray-50"
            >
              <div className="text-sm font-medium text-gray-800">Use Sales &amp; Targets</div>
              <div className="text-xs text-gray-500">Sold ÷ target, from Sales &amp; Targets.</div>
            </button>
          )}
        </section>
      )}

      {/* Sub-metrics */}
      <section>
        <div className="mb-1.5 flex items-center justify-between">
          <SectionLabel className="mb-0">Sub-metrics</SectionLabel>
          {activeEditor !== 'new' && (
            <button
              onClick={() => setActiveEditor('new')}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="h-3.5 w-3.5" /> Add sub-metric
            </button>
          )}
        </div>
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          {loading ? (
            <BlockLoader className="py-6" />
          ) : draftSubs.length === 0 && activeEditor !== 'new' ? (
            <p className="p-3 text-xs text-gray-500">
              No sub-metrics yet. Add a leg like <code className="rounded bg-gray-100 px-1">Target</code>{' '}
              or <code className="rounded bg-gray-100 px-1">Achievement</code> to capture richer data,
              then write a formula combining them.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {draftSubs.map((s) => (
                <li key={s.id}>
                  {activeEditor === s.id ? (
                    <SubMetricEditor
                      initial={s}
                      eligibleQuestions={eligibleQuestions}
                      salesProducts={salesProducts}
                      siblings={draftSubs.filter((o) => o.id !== s.id)}
                      onCancel={() => setActiveEditor(null)}
                      onCommit={(data) => commitEditSub(s.id, data)}
                    />
                  ) : (
                    <SubMetricDisplay
                      sub={s}
                      onEdit={() => setActiveEditor(s.id)}
                      onDelete={() => deleteSub(s)}
                    />
                  )}
                </li>
              ))}
              {activeEditor === 'new' && (
                <li>
                  <SubMetricEditor
                    initial={null}
                    eligibleQuestions={eligibleQuestions}
                    salesProducts={salesProducts}
                    siblings={draftSubs}
                    onCancel={() => setActiveEditor(null)}
                    onCommit={commitNewSub}
                  />
                </li>
              )}
            </ul>
          )}
        </div>
        {/* While an editor is open it shows this line itself, from what is being
            picked there, so the line is on screen once and never twice. */}
        {activeEditor === null && salesProductsMismatch(draftSubs) && (
          <p className="mt-1.5 text-xs text-amber-700">{SALES_PRODUCTS_MISMATCH_TEXT}</p>
        )}
      </section>

      {/* Cohort grouping — only meaningful once at least one sub-metric
          exists since cohort_*() needs a sub-metric to aggregate over.
          Kept above Formula so the admin sets the peer group before
          referencing it in cohort_median(...) etc. */}
      <section>
        <SectionLabel>Cohort grouping</SectionLabel>
        <CohortGroupingPicker value={draftCohort} onChange={setDraftCohort} />
      </section>

      {/* Formula — chip-based builder. No free text; the admin assembles
          the expression by clicking sub-metric chips, operator buttons,
          parentheses, and a number-add field. The result serialises to
          the same string the engine consumes. */}
      <section>
        <SectionLabel>Formula</SectionLabel>
        <FormulaBuilder
          formula={draftFormula}
          onChange={setDraftFormula}
          availableSubs={draftSubs.map((s) => ({ code: s.code, name: s.name }))}
          error={formulaErr}
          disabled={isNew && draftSubs.length === 0}
          // Under the Sales & Targets card the formula is fixed, so the box says
          // what it is instead of asking for sub-metrics.
          disabledText={simpleSalesFeed ? 'Sold ÷ target × 100.' : undefined}
        />
      </section>

      {/* Footer action bar */}
      <div className="flex items-center justify-end gap-2 border-t border-gray-200 pt-3">
        {/* Save is off while the formula is wrong, and the formula's own line can be a
            long way up this window, so the reason is said beside the button. */}
        {formulaErr ? (
          <span role="alert" className="mr-auto text-[11px] text-red-600">Can&apos;t save yet. {formulaErr}</span>
        ) : isDirty && !isNew && (
          <span className="mr-auto text-[11px] text-amber-700">Unsaved changes</span>
        )}
        <button
          onClick={cancel}
          disabled={saving}
          className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={!canSave}
          className="inline-flex shrink-0 items-center whitespace-nowrap gap-1.5 rounded-md bg-primary-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {saving ? 'Saving…' : isNew ? 'Create metric' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SubMetricDisplay — read-only row (name · code · source badge · edit/del)
// ─────────────────────────────────────────────────────────────────────────────

interface SubMetricDisplayProps {
  sub: { id: string; name: string; code: string; source_type: SubMetricSourceType; valid_min?: number | null; valid_max?: number | null };
  onEdit: () => void;
  onDelete: () => void;
}

// Source badge of a sub-metric row: how its values arrive.
const SOURCE_BADGE: Record<SubMetricSourceType, { label: string; className: string }> = {
  manual:        { label: 'manual',          className: 'bg-gray-100 text-gray-600' },
  task_question: { label: 'auto-derived',    className: 'bg-emerald-50 text-emerald-700' },
  sales_targets: { label: 'Sales & Targets', className: 'bg-blue-50 text-blue-700' },
};

function SubMetricDisplay({ sub, onEdit, onDelete }: SubMetricDisplayProps) {
  const range = formatValidRange(sub.valid_min, sub.valid_max);
  // A source this build does not know reads as manual, as it did before the badge had three values.
  const badge = SOURCE_BADGE[sub.source_type] ?? SOURCE_BADGE.manual;
  return (
    <div className="flex items-center justify-between px-3 py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        {/* Slug code chip (e.g. `test_2`) intentionally omitted — it's a
            DB-side identifier exposed to the parser; admins don't need
            to see it on the sub-metric list. The source badge below
            still tells them how the leg is sourced. */}
        <span className="truncate text-sm font-medium text-gray-800">{sub.name}</span>
        <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${badge.className}`}>
          {badge.label}
        </span>
        {range && (
          <span
            className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600"
            title="Values outside this range are ignored when scoring, as if there were no value."
          >
            valid {range}
          </span>
        )}
      </div>
      <div className="flex flex-shrink-0 items-center gap-0.5">
        <button onClick={onEdit} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700" aria-label="Edit">
          <Edit3 className="h-3.5 w-3.5" />
        </button>
        <button onClick={onDelete} className="rounded-md p-1.5 text-rose-500 hover:bg-rose-50 hover:text-rose-700" aria-label="Delete">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SubMetricEditor — used for both adding and editing in-place. Pure
// controlled component: bubbles the final values up via onCommit; no
// API calls. The parent decides whether this means "add to staging" or
// "replace existing in staging".
// ─────────────────────────────────────────────────────────────────────────────

/** What the sub-metric editor hands back on Confirm. */
type SubMetricEditorOutput = {
  name: string;
  source_type: SubMetricSourceType;
  source_question_id: string | null;
  source_aggregation: 'sum' | 'avg' | 'latest' | null;
  source_target_picker_question_id: string | null;
  /** Sales & Targets only; null on every other source. */
  source_sales_figure: SalesFeedFigure | null;
  source_sales_products: string[] | null;
  valid_min: number | null;
  valid_max: number | null;
};

interface SubMetricEditorProps {
  initial: { name: string; source_type: SubMetricSourceType; source_question_id: string | null; source_aggregation: 'sum' | 'avg' | 'latest' | null; source_target_picker_question_id?: string | null; source_sales_figure?: SalesFeedFigure | null; source_sales_products?: string[] | null; valid_min?: number | null; valid_max?: number | null } | null;
  eligibleQuestions: EligibleSourceQuestion[];
  /** The project's Sales & Targets products; empty = that source is not offered. */
  salesProducts: SalesProductOption[];
  /** The metric's OTHER sub-metrics, to say when Target and Sold count different products. */
  siblings: SalesFeedFields[];
  onCancel: () => void;
  onCommit: (data: SubMetricEditorOutput) => void;
}

const SOURCE_SWITCH: Array<{ type: SubMetricSourceType; label: string }> = [
  { type: 'manual', label: 'Manual' },
  { type: 'task_question', label: 'Task question' },
  { type: 'sales_targets', label: 'Sales & Targets' },
];

// 'sales' is the stored name of the figure; Sales & Targets itself calls it Sold.
const FIGURE_SWITCH: Array<{ figure: SalesFeedFigure; label: string }> = [
  { figure: 'target', label: 'Target' },
  { figure: 'sales', label: 'Sold' },
];

function SubMetricEditor({ initial, eligibleQuestions, salesProducts, siblings, onCancel, onCommit }: SubMetricEditorProps) {
  // Value-question dropdown excludes pickers; the picker dropdown lists
  // only store_employee_picker questions. Both come from the same
  // eligible-questions endpoint, separated here by question_type.
  const valueQuestions = eligibleQuestions.filter((q) => q.question_type !== 'store_employee_picker');
  const pickerQuestions = eligibleQuestions.filter((q) => q.question_type === 'store_employee_picker');

  const [name, setName] = useState<string>(initial?.name ?? '');
  const [sourceType, setSourceType] = useState<SubMetricSourceType>(initial?.source_type ?? 'manual');
  const [questionId, setQuestionId] = useState<string>(initial?.source_question_id ?? valueQuestions[0]?.id ?? '');
  const [agg, setAgg] = useState<'sum' | 'avg' | 'latest'>(initial?.source_aggregation ?? 'sum');
  // '' = "Score the submitter" (legacy attribution). Otherwise a picker
  // question id whose answer names the rated employee.
  const [pickerQuestionId, setPickerQuestionId] = useState<string>(initial?.source_target_picker_question_id ?? '');
  // Sales & Targets: nothing pre-selected for the figure. A default would be
  // right for one of a metric's two inputs and silently wrong for the other
  // (Target ÷ Target reads 100% for everybody). Products: null = all products.
  const [figure, setFigure] = useState<SalesFeedFigure | null>(initial?.source_sales_figure ?? null);
  const [products, setProducts] = useState<string[] | null>(initial?.source_sales_products ?? null);
  // Valid range as typed; '' = no limit on that side.
  const [minText, setMinText] = useState<string>(initial?.valid_min == null ? '' : String(initial.valid_min));
  const [maxText, setMaxText] = useState<string>(initial?.valid_max == null ? '' : String(initial.valid_max));
  const [err, setErr] = useState<string | null>(null);

  // Only offer pickers that live in the SAME task as the selected value
  // question — pairing happens within a submission, so a picker from
  // another task could never match.
  const selectedValueTaskId = valueQuestions.find((q) => q.id === questionId)?.task_id ?? null;
  const samePickerQuestions = selectedValueTaskId
    ? pickerQuestions.filter((q) => q.task_id === selectedValueTaskId)
    : pickerQuestions;

  const fromSales = sourceType === 'sales_targets';
  // Confirm stays off until the figure is picked and, for "Choose products",
  // something is ticked: either gap would save an input that counts nothing.
  const salesIncomplete = fromSales && (figure === null || noSalesProductsPicked(products));
  // This input as it stands in the editor, beside the metric's other inputs
  // (left out while it is half-picked: nothing ticked yet is not "different").
  const productsMismatch = salesProductsMismatch(
    fromSales && !salesIncomplete
      ? [...siblings, { source_type: sourceType, source_sales_figure: figure, source_sales_products: products }]
      : siblings,
  );

  const commit = () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    if (sourceType === 'task_question' && !questionId) {
      setErr('Pick a task question'); return;
    }
    if (fromSales) {
      // Enter in the name box reaches here past the disabled Confirm button.
      if (salesIncomplete) return;
      // No valid range: a manager's figure is the team's total, which any range
      // sized for one person would silently drop.
      onCommit({
        name: name.trim(),
        source_type: 'sales_targets',
        source_question_id: null,
        source_aggregation: null,
        source_target_picker_question_id: null,
        source_sales_figure: figure,
        source_sales_products: products,
        valid_min: null,
        valid_max: null,
      });
      return;
    }
    const validMin = minText.trim() === '' ? null : Number(minText);
    const validMax = maxText.trim() === '' ? null : Number(maxText);
    if ((validMin !== null && !Number.isFinite(validMin)) || (validMax !== null && !Number.isFinite(validMax))) {
      setErr('The valid range must be numbers'); return;
    }
    if (validMin !== null && validMax !== null && validMin > validMax) {
      setErr('The valid range minimum is above the maximum'); return;
    }
    // Guard against a stale picker: only keep it if it still belongs to the
    // selected value question's task (the value question may have changed
    // after the picker was chosen).
    const validPicker = sourceType === 'task_question'
      && pickerQuestionId
      && samePickerQuestions.some((q) => q.id === pickerQuestionId)
      ? pickerQuestionId
      : null;
    onCommit({
      name: name.trim(),
      source_type: sourceType,
      source_question_id: sourceType === 'task_question' ? (questionId || null) : null,
      source_aggregation: sourceType === 'task_question' ? agg : null,
      source_target_picker_question_id: validPicker,
      source_sales_figure: null,
      source_sales_products: null,
      valid_min: validMin,
      valid_max: validMax,
    });
  };

  return (
    <div className="bg-gray-50/80 p-3 space-y-2">
      {err && <p className="text-xs text-red-600">{err}</p>}
      <div className="flex items-center gap-2">
        <input
          type="text"
          autoFocus
          placeholder="Sub-metric name (e.g. Target, Achievement)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') onCancel(); }}
          className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
        />
        <div className="inline-flex flex-shrink-0 items-center gap-0.5 rounded-md border border-gray-300 bg-white p-0.5 text-xs">
          {SOURCE_SWITCH.map(({ type: t, label }) => {
            const active = sourceType === t;
            // An input already on Sales & Targets keeps its button even if the
            // project's products could not be read just now.
            const disabled = (t === 'task_question' && eligibleQuestions.length === 0)
              || (t === 'sales_targets' && salesProducts.length === 0 && !active);
            return (
              <button
                key={t}
                type="button"
                disabled={disabled}
                aria-pressed={active}
                onClick={() => setSourceType(t)}
                className={`rounded px-2.5 py-1 transition ${
                  active
                    ? 'bg-primary-600 text-white shadow-sm'
                    : disabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <button
          onClick={onCancel}
          aria-label="Cancel"
          title="Cancel"
          className="rounded-md p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
        >
          <X className="h-4 w-4" />
        </button>
        <button
          onClick={commit}
          disabled={salesIncomplete}
          aria-label="Confirm"
          title="Confirm"
          className="inline-flex items-center rounded-md bg-primary-600 p-1.5 text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Check className="h-4 w-4" />
        </button>
      </div>
      {fromSales && (
        <>
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Figure</label>
            <div className="inline-flex items-center gap-0.5 rounded-md border border-gray-300 bg-white p-0.5 text-xs">
              {FIGURE_SWITCH.map(({ figure: f, label }) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={figure === f}
                  onClick={() => setFigure(f)}
                  className={`rounded px-2.5 py-1 transition ${
                    figure === f ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {/* With no product list to show (none, or the read failed) every saved
              product would read as gone, so the ticks are left as they are. */}
          <SalesTargetsProductsField
            options={salesProducts}
            value={products}
            onChange={setProducts}
            disabled={salesProducts.length === 0}
          />
        </>
      )}
      {productsMismatch && (
        <p className="text-xs text-amber-700">{SALES_PRODUCTS_MISMATCH_TEXT}</p>
      )}
      {sourceType === 'task_question' && (
        <>
          <div className="grid grid-cols-[1fr_120px] gap-2">
            <select
              value={questionId}
              onChange={(e) => setQuestionId(e.target.value)}
              className="min-w-0 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
            >
              <option value="">— pick a question —</option>
              {valueQuestions.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.task_title} · {q.question_text.slice(0, 60)}{q.question_text.length > 60 ? '…' : ''}
                </option>
              ))}
            </select>
            <select
              value={agg}
              onChange={(e) => setAgg(e.target.value as 'sum' | 'avg' | 'latest')}
              className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
            >
              <option value="sum">Sum</option>
              <option value="avg">Average</option>
              <option value="latest">Latest</option>
            </select>
          </div>
          {/* Rated-employee picker. Only shown when the task carries a
              store_employee_picker question — for feedback tasks (e.g. an
              AM rating an iPro) the answer must be attributed to the picked
              employee, not the submitter. Default scores the submitter. */}
          {samePickerQuestions.length > 0 && (
            <div className="space-y-1">
              <label className="block text-[11px] font-medium uppercase tracking-wide text-gray-500">
                Rated employee from
              </label>
              <select
                value={pickerQuestionId}
                onChange={(e) => setPickerQuestionId(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
              >
                <option value="">Score the submitting employee (default)</option>
                {samePickerQuestions.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.task_title} · {q.question_text.slice(0, 60)}{q.question_text.length > 60 ? '…' : ''}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-gray-400">
                Attributes the value to the employee selected in this picker question — paired per submission.
              </p>
            </div>
          )}
        </>
      )}
      {/* Valid range — e.g. 0–100 for a score, so a typo like 1716044 can't
          distort the cohort median / spread. Not offered for Sales & Targets:
          nobody types those figures, and a manager's is a team total. */}
      {!fromSales && (
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Valid range</label>
        <input
          type="number"
          inputMode="decimal"
          placeholder="Min"
          aria-label="Valid range minimum"
          value={minText}
          onChange={(e) => setMinText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') onCancel(); }}
          className="w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
        />
        <span className="text-xs text-gray-400">to</span>
        <input
          type="number"
          inputMode="decimal"
          placeholder="Max"
          aria-label="Valid range maximum"
          value={maxText}
          onChange={(e) => setMaxText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') onCancel(); }}
          className="w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
        />
        <span className="text-[11px] text-gray-400">
          Optional. Values outside it are ignored when scoring, as if there were no value.
        </span>
      </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Small layout primitives — uppercase section labels + radio card.
// ─────────────────────────────────────────────────────────────────────────────

function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  // A caller's own mb-* replaces the default margin. Both on one element resolve by the
  // order Tailwind emits them, not the order written: mb-1.5 comes after mb-0, so a label
  // asked to sit flush in a header row kept its margin and rode above the row's centre.
  const ownMargin = (className ?? '').split(/\s+/).some((c) => /^mb-/.test(c));
  return (
    <div className={`${ownMargin ? '' : 'mb-1.5 '}text-[10px] font-semibold uppercase tracking-wider text-gray-500 ${className ?? ''}`}>
      {children}
    </div>
  );
}

interface RadioCardProps {
  checked: boolean;
  onChange: () => void;
  title: string;
  description: string;
  disabled?: boolean;
  children?: React.ReactNode;
}

function RadioCard({ checked, onChange, title, description, disabled, children }: RadioCardProps) {
  return (
    <label className={`flex items-start gap-2.5 rounded-md border px-3 py-2 transition ${
      checked
        ? 'border-primary-300 bg-primary-50/40'
        : 'border-gray-200 hover:bg-gray-50'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
      <input
        type="radio"
        className="mt-0.5 accent-primary-600"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-800">{title}</div>
        <div className="text-xs text-gray-500">{description}</div>
        {children}
      </div>
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FormulaBuilder — chip-based assembly for the metric formula.
// ─────────────────────────────────────────────────────────────────────────────
//
// Free-text formula entry was too error-prone for non-engineer admins —
// typos, wrong identifiers, mismatched parens, etc. This component
// renders the formula as a row of clickable chips and assembles new
// tokens via labelled buttons (sub-metric chips, operators, parens,
// number input). The serialised output is the same expression string
// the server parser already understands.

// Reserved identifiers — the tokenizer recognises these as keywords so
// they round-trip correctly through chip-based editing and so the
// rendering can colour them distinctly from sub-metric references.
const LOGIC_KEYWORDS = new Set(['and', 'or', 'not']);
const FUNCTION_KEYWORDS = new Set(['if', 'cohort_median', 'cohort_stddev', 'cohort_count']);
const IMPLICIT_REFS = new Set(['aon_months', 'submitted_this_period', 'cohort_size']);

type Token =
  | { kind: 'ref'; code: string }                                     // sub-metric reference
  | { kind: 'implicit'; code: string }                                // aon_months etc.
  | { kind: 'num'; value: number }
  | { kind: 'bool'; value: boolean }
  | { kind: 'null' }
  | { kind: 'op'; op: '+' | '-' | '*' | '/' }
  | { kind: 'cmp'; op: '>' | '>=' | '<' | '<=' | '==' | '!=' }
  | { kind: 'logic'; op: 'and' | 'or' | 'not' }
  | { kind: 'paren'; side: '(' | ')' }
  | { kind: 'comma' }
  | { kind: 'fn'; name: 'if' | 'cohort_median' | 'cohort_stddev' | 'cohort_count' };

/** Parse the stored formula string back into tokens for the editor.
 *  Mirrors the server tokenizer in `apps/api/src/utils/metricFormula.ts`. */
const tokenize = (formula: string): Token[] => {
  const out: Token[] = [];
  let i = 0;
  const src = formula;
  while (i < src.length) {
    while (i < src.length && /\s/.test(src[i])) i++;
    if (i >= src.length) break;
    const two = src.slice(i, i + 2);
    if (two === '>=' || two === '<=' || two === '==' || two === '!=') {
      out.push({ kind: 'cmp', op: two as '>=' | '<=' | '==' | '!=' });
      i += 2;
      continue;
    }
    const c = src[i];
    if (c === '+' || c === '-' || c === '*' || c === '/') {
      out.push({ kind: 'op', op: c });
      i++;
    } else if (c === '>' || c === '<') {
      out.push({ kind: 'cmp', op: c });
      i++;
    } else if (c === '(' || c === ')') {
      out.push({ kind: 'paren', side: c });
      i++;
    } else if (c === ',') {
      out.push({ kind: 'comma' });
      i++;
    } else if (/[0-9.]/.test(c)) {
      const start = i;
      while (i < src.length && /[0-9.]/.test(src[i])) i++;
      const v = Number(src.slice(start, i));
      if (Number.isFinite(v)) out.push({ kind: 'num', value: v });
    } else if (/[a-zA-Z_]/.test(c)) {
      const start = i;
      while (i < src.length && /[a-zA-Z0-9_]/.test(src[i])) i++;
      const word = src.slice(start, i).toLowerCase();
      if (word === 'true')  out.push({ kind: 'bool', value: true });
      else if (word === 'false') out.push({ kind: 'bool', value: false });
      else if (word === 'null')  out.push({ kind: 'null' });
      else if (LOGIC_KEYWORDS.has(word)) {
        out.push({ kind: 'logic', op: word as 'and' | 'or' | 'not' });
      } else if (FUNCTION_KEYWORDS.has(word)) {
        out.push({ kind: 'fn', name: word as 'if' | 'cohort_median' | 'cohort_stddev' | 'cohort_count' });
      } else if (IMPLICIT_REFS.has(word)) {
        out.push({ kind: 'implicit', code: word });
      } else {
        out.push({ kind: 'ref', code: word });
      }
    } else {
      i++; // ignore unknown char
    }
  }
  return out;
};

/** Serialise tokens back to the formula string the server consumes. */
const serializeTokens = (tokens: Token[]): string => tokens.map((t) => {
  switch (t.kind) {
    case 'ref':      return t.code;
    case 'implicit': return t.code;
    case 'num':      return String(t.value);
    case 'bool':     return t.value ? 'true' : 'false';
    case 'null':     return 'null';
    case 'op':       return t.op;
    case 'cmp':      return t.op;
    case 'logic':    return t.op;
    case 'paren':    return t.side;
    case 'comma':    return ',';
    case 'fn':       return t.name;
  }
}).join(' ');

// ─────────────────────────────────────────────────────────────────────────────
// Cohort grouping picker
// ─────────────────────────────────────────────────────────────────────────────
//
// Lets the admin describe the peer group used by the cohort_*() formula
// functions. Composite groupings stack 1-4 single-kind pickers; the
// AON kind shows an inline bucket editor with one row per bracket.
//
// The picker always emits a normalised value matching
// `CohortGrouping` above so DetailScreen's diff logic can compare it
// to the server snapshot with `JSON.stringify` equality.

interface CohortGroupingPickerProps {
  value: CohortGrouping;
  onChange: (next: CohortGrouping) => void;
}

/** Normalise into composite form so the editor can treat single + composite
 *  identically — a single grouping is just a one-dim composite under the
 *  hood. Saved back as single when only one dim remains. */
function asGroups(g: CohortGrouping): SingleCohortGrouping[] {
  return g.kind === 'composite' ? g.groups : [g];
}
function fromGroups(gs: SingleCohortGrouping[]): CohortGrouping {
  if (gs.length === 0) return { kind: 'project' };
  if (gs.length === 1) return gs[0];
  return { kind: 'composite', groups: gs };
}

const SINGLE_KIND_OPTIONS: Array<{ kind: SingleCohortGrouping['kind']; label: string; description: string }> = [
  { kind: 'project',        label: 'Project-wide',      description: 'One peer group for the whole project.' },
  { kind: 'aon_buckets',    label: 'By AON bucket',     description: 'Group employees by months on the network.' },
  { kind: 'role',           label: 'By role',           description: 'Compare employees only against same-role peers.' },
  { kind: 'store_category', label: 'By store category', description: 'Cohorts cut by the store category code.' },
];

function CohortGroupingPicker({ value, onChange }: CohortGroupingPickerProps) {
  const groups = asGroups(value);

  const setGroup = (idx: number, next: SingleCohortGrouping) => {
    const copy = [...groups]; copy[idx] = next;
    onChange(fromGroups(copy));
  };
  const removeGroup = (idx: number) => {
    const copy = groups.filter((_, i) => i !== idx);
    onChange(fromGroups(copy));
  };
  const addGroup = () => {
    if (groups.length >= 4) return;
    onChange(fromGroups([...groups, { kind: 'project' }]));
  };

  // Default to a fresh AON buckets shape when the admin switches an
  // existing slot from another kind. Saves a click for the most common
  // case (which is also the seed-score-style use).
  const switchKind = (idx: number, kind: SingleCohortGrouping['kind']) => {
    let next: SingleCohortGrouping;
    switch (kind) {
      case 'project':        next = { kind: 'project' }; break;
      case 'role':           next = { kind: 'role' }; break;
      case 'store_category': next = { kind: 'store_category' }; break;
      case 'aon_buckets':
        next = { kind: 'aon_buckets', honeymoon_months: 3, buckets: SEED_AON_BUCKETS };
        break;
    }
    setGroup(idx, next);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
      <p className="text-[11px] text-gray-500">
        Defines the peer group used by <code className="rounded bg-gray-100 px-1">cohort_median</code>,
        {' '}<code className="rounded bg-gray-100 px-1">cohort_stddev</code>,
        {' '}<code className="rounded bg-gray-100 px-1">cohort_count</code> in the formula.
        Stack up to four dimensions to slice by their cartesian product (e.g. AON × role).
      </p>

      <div className="space-y-2">
        {groups.map((g, idx) => (
          <div key={idx} className="rounded-md border border-gray-200 bg-gray-50/50 p-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                Dim {idx + 1}
              </span>
              <select
                value={g.kind}
                onChange={(e) => switchKind(idx, e.target.value as SingleCohortGrouping['kind'])}
                className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
              >
                {SINGLE_KIND_OPTIONS.map((opt) => (
                  <option key={opt.kind} value={opt.kind}>{opt.label}</option>
                ))}
              </select>
              {groups.length > 1 && (
                <button
                  onClick={() => removeGroup(idx)}
                  className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                  aria-label={`Remove dimension ${idx + 1}`}
                  title="Remove dimension"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <p className="mt-1 text-[11px] text-gray-500">
              {SINGLE_KIND_OPTIONS.find((o) => o.kind === g.kind)?.description}
            </p>
            {g.kind === 'aon_buckets' && (
              <AonBucketsEditor
                value={g}
                onChange={(next) => setGroup(idx, next)}
              />
            )}
          </div>
        ))}
      </div>

      {groups.length < 4 ? (
        <button
          onClick={addGroup}
          className="inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          <Plus className="h-3 w-3" /> Add dimension
        </button>
      ) : (
        <p className="text-[11px] text-gray-400">Up to 4 dimensions per cohort.</p>
      )}

      <p className="text-[11px] text-gray-500">
        Summary: <span className="font-medium text-gray-700">{summariseCohort(value)}</span>
      </p>
    </div>
  );
}

interface AonBucketsEditorProps {
  value: AonBucketsGrouping;
  onChange: (next: AonBucketsGrouping) => void;
}

/** Inline editor for the buckets list of an `aon_buckets` cohort.
 *  Validation is best-effort here — the server runs the authoritative
 *  check on save and surfaces any gap / overlap / ordering issue. */
function AonBucketsEditor({ value, onChange }: AonBucketsEditorProps) {
  const honeymoon = value.honeymoon_months ?? 0;
  const setBucket = (idx: number, patch: Partial<AonBucket>) => {
    const buckets = value.buckets.map((b, i) => i === idx ? { ...b, ...patch } : b);
    onChange({ ...value, buckets });
  };
  const removeBucket = (idx: number) => {
    onChange({ ...value, buckets: value.buckets.filter((_, i) => i !== idx) });
  };
  const addBucket = () => {
    const last = value.buckets[value.buckets.length - 1];
    const nextLow = last ? (last.high_months ?? last.low_months + 1) : honeymoon;
    onChange({
      ...value,
      buckets: [...value.buckets, { name: `${nextLow}m+`, low_months: nextLow, high_months: null }],
    });
  };

  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex items-center gap-2 text-[11px] text-gray-600">
        <label className="inline-flex items-center gap-1">
          Honeymoon &lt;
          <input
            type="number"
            min={0}
            value={honeymoon}
            onChange={(e) => onChange({ ...value, honeymoon_months: Number(e.target.value) || 0 })}
            className="w-14 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs focus:border-primary-500 focus:outline-none"
          />
          months — full credit, skips the bucket logic.
        </label>
      </div>
      <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 bg-white">
        {value.buckets.map((b, idx) => {
          const isLast = idx === value.buckets.length - 1;
          return (
            <li key={idx} className="flex items-center gap-1.5 px-2 py-1 text-xs">
              <input
                type="text"
                value={b.name}
                onChange={(e) => setBucket(idx, { name: e.target.value })}
                placeholder="name"
                className="w-20 rounded border border-gray-300 px-1.5 py-0.5 focus:border-primary-500 focus:outline-none"
              />
              <input
                type="number"
                min={0}
                value={b.low_months}
                onChange={(e) => setBucket(idx, { low_months: Number(e.target.value) || 0 })}
                className="w-16 rounded border border-gray-300 px-1.5 py-0.5 focus:border-primary-500 focus:outline-none"
                title="low_months (inclusive)"
              />
              <span className="text-gray-400">→</span>
              <input
                type="number"
                min={0}
                value={b.high_months ?? ''}
                placeholder={isLast ? '∞' : ''}
                onChange={(e) => setBucket(idx, {
                  high_months: e.target.value === '' ? null : Number(e.target.value),
                })}
                className="w-16 rounded border border-gray-300 px-1.5 py-0.5 focus:border-primary-500 focus:outline-none"
                title="high_months (exclusive; blank = unbounded — last bucket only)"
              />
              <span className="text-gray-400">m</span>
              <button
                onClick={() => removeBucket(idx)}
                disabled={value.buckets.length <= 1}
                className="ml-auto rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                aria-label="Remove bucket"
                title="Remove bucket"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          );
        })}
      </ul>
      <button
        onClick={addBucket}
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50"
      >
        <Plus className="h-3 w-3" /> Add bucket
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Formula presets
// ─────────────────────────────────────────────────────────────────────────────
//
// The chip builder is fine for short formulas (`achievement / target * 100`)
// but cruel for piecewise stacks (Seed Score: six nested if()s, ~50 clicks
// and easy to lose track of which `,` you're filling). Presets let an admin
// pick a known-good recipe and fill in the parameters instead of assembling
// the structure. Each preset declares the sub-metric "slots" it needs and a
// formula template using $slot placeholders; the picker substitutes the
// admin's chosen sub-metric codes at apply time. If the project's sub-metrics
// don't match a preset's slot count, the picker disables the button with a
// hint to add the missing leg.
//
// Each preset's `formula` body is a regular formula string — the parser
// re-validates after substitution, so a preset that wouldn't pass on its
// own can't slip through.

interface FormulaPresetSlot {
  /** Placeholder name used in the formula template (e.g. "value"). */
  placeholder: string;
  /** Label admin sees in the slot picker (e.g. "Value sub-metric"). */
  label: string;
  /** Optional hint about which sub-metric to pick. */
  hint?: string;
}

interface FormulaPreset {
  id: string;
  label: string;
  /** One-line plain-English summary, plain enough to read at a glance. */
  description: string;
  /** Slots the admin maps to actual sub-metric codes. */
  slots: FormulaPresetSlot[];
  /** Formula template using $slot tokens that get replaced on apply. */
  template: string;
  /** Whether the preset uses cohort_*() — used to nudge the admin
   *  toward setting a non-trivial Cohort grouping above. */
  needsCohortGrouping?: boolean;
}

const FORMULA_PRESETS: FormulaPreset[] = [
  {
    id: 'cohort-tiering',
    label: 'Cohort-relative tiering (Seed Score style)',
    description:
      'Compare each employee to their peer-group median and σ. Honeymoon (new joiners) and singleton cohorts get full points; otherwise award 20 / 15 / 10 / 0 based on distance from the median.',
    slots: [{ placeholder: 'value', label: 'Value', hint: 'The number being compared against the cohort.' }],
    needsCohortGrouping: true,
    template:
      'if(aon_months < 3, 20,\n' +
      '  if(cohort_count($value) <= 1, 20,\n' +
      '    if(submitted_this_period == false, 0,\n' +
      '      if($value > cohort_median($value) + cohort_stddev($value), 20,\n' +
      '        if($value >= cohort_median($value), 15,\n' +
      '          if($value >= cohort_median($value) - cohort_stddev($value), 10, 0))))))',
  },
  {
    id: 'achievement-pct',
    label: 'Achievement % of target',
    description: 'Classic ratio: (achievement ÷ target) × 100. The slabs map the % to points.',
    slots: [
      { placeholder: 'achievement', label: 'Achievement' },
      { placeholder: 'target',      label: 'Target' },
    ],
    template: '$achievement / $target * 100',
  },
  {
    id: 'pass-fail',
    label: 'Pass / fail at threshold',
    description: 'Award full points if the value meets a threshold; otherwise zero. Useful for binary metrics like "submitted on time".',
    slots: [{ placeholder: 'value', label: 'Value' }],
    template: 'if($value >= 1, 100, 0)',
  },
  {
    id: 'inverse-pct',
    label: 'Inverse % (lower is better)',
    description: '100 − (achievement ÷ target) × 100. Use when smaller is better and you want the slab table to read top-down like an upper bound.',
    slots: [
      { placeholder: 'achievement', label: 'Achievement' },
      { placeholder: 'target',      label: 'Target' },
    ],
    template: '100 - $achievement / $target * 100',
  },
];

/** Apply a preset by substituting each slot's $placeholder with the
 *  admin's chosen sub-metric code. Returns null if any slot is unmapped. */
function applyPreset(preset: FormulaPreset, slotMap: Record<string, string>): string | null {
  let out = preset.template;
  for (const s of preset.slots) {
    const code = slotMap[s.placeholder];
    if (!code) return null;
    // Replace every $placeholder (whole-word) with the chosen code. Anchor
    // on a non-word boundary so $value doesn't accidentally match $value2.
    out = out.replace(new RegExp('\\$' + s.placeholder + '\\b', 'g'), code);
  }
  return out;
}

interface FormulaBuilderProps {
  formula: string;
  onChange: (next: string) => void;
  /** Sub-metrics in their staged display order. The slug `code` is what
   *  the parser consumes (formula serialises to `target + achievement`);
   *  the `name` is what we show on the chips / picker buttons so admins
   *  see the labels they typed. */
  availableSubs: Array<{ code: string; name: string }>;
  error: string | null;
  disabled?: boolean;
  /** What the disabled box says instead of its usual explainer. */
  disabledText?: string;
}

function FormulaBuilder({ formula, onChange, availableSubs, error, disabled, disabledText }: FormulaBuilderProps) {
  // code → name lookup so chip rendering can show the human label
  // wherever a ref token appears in the assembled formula.
  const nameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of availableSubs) m.set(s.code.toLowerCase(), s.name);
    return m;
  }, [availableSubs]);
  // Tokenise once per formula change. The editor mutates `tokens` and
  // serialises back to a string on every change so the parent sees a
  // standard formula string and the existing save path doesn't care
  // about the intermediate representation.
  const tokens: Token[] = useMemo(() => tokenize(formula), [formula]);

  const setTokens = (next: Token[]) => onChange(serializeTokens(next));
  const append = (t: Token) => setTokens([...tokens, t]);
  const removeAt = (idx: number) => setTokens(tokens.filter((_, i) => i !== idx));
  const clear = () => setTokens([]);
  const backspace = () => setTokens(tokens.slice(0, -1));

  const [numInput, setNumInput] = useState('');
  const addNumber = () => {
    const v = Number(numInput);
    if (!Number.isFinite(v)) return;
    append({ kind: 'num', value: v });
    setNumInput('');
  };

  // ── Mode toggle: chip builder (default) vs raw text. Text mode is a
  //    one-click escape hatch for power users who want to paste a formula
  //    or edit it as a string. Switching back retokenizes the text into
  //    chips automatically (tokenize() round-trips).
  type Mode = 'chips' | 'text';
  const [mode, setMode] = useState<Mode>('chips');

  // ── Preset picker state: open by default ONLY when the formula is
  //    empty (new metric or freshly cleared) — admins editing an
  //    existing formula keep their chip view; they can re-open presets
  //    from the pill in the header. Tracked as a one-shot ref so a
  //    later edit that empties the formula doesn't reopen the banner.
  const [presetsOpen, setPresetsOpen] = useState<boolean>(() => formula.trim() === '');
  // Per-preset slot mapping: which sub-metric code fills each $placeholder.
  // Defaulted on open to the best-guess match (same code or same name).
  const [openPresetId, setOpenPresetId] = useState<string | null>(null);
  const [slotMap, setSlotMap] = useState<Record<string, string>>({});

  const guessSubForSlot = (slot: FormulaPresetSlot): string => {
    // Look for an exact code match first (admin named their sub-metric
    // "value" / "achievement" / "target"); then fall back to the first
    // available sub-metric so the picker never starts empty.
    const exact = availableSubs.find((s) => s.code.toLowerCase() === slot.placeholder.toLowerCase());
    if (exact) return exact.code;
    return availableSubs[0]?.code ?? '';
  };
  const openPreset = (id: string) => {
    const p = FORMULA_PRESETS.find((x) => x.id === id);
    if (!p) return;
    setOpenPresetId(id);
    const next: Record<string, string> = {};
    for (const slot of p.slots) next[slot.placeholder] = guessSubForSlot(slot);
    setSlotMap(next);
  };
  const applyOpenPreset = () => {
    const p = FORMULA_PRESETS.find((x) => x.id === openPresetId);
    if (!p) return;
    const compiled = applyPreset(p, slotMap);
    if (!compiled) return;
    onChange(compiled);
    setOpenPresetId(null);
    setPresetsOpen(false);
  };

  if (disabled) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 text-xs text-gray-500">
        {disabledText ?? (
          <>
            Add at least one sub-metric to build a formula. For a single-value
            metric, leave this empty — the simple value-source picker above
            handles the formula automatically on save.
          </>
        )}
      </div>
    );
  }

  // The chip section + button stack only render in chip mode. In text
  // mode we collapse to a textarea so the whole card stays compact and
  // the admin isn't distracted by two parallel editors at once.
  const isText = mode === 'text';

  // The preset banner gets prominence: it auto-opens on an empty formula
  // and the picker shows full descriptions inline. Once the admin either
  // applies a preset OR starts typing chips, the banner collapses to a
  // single "Start from preset" pill they can re-open.
  const noPresetable = availableSubs.length === 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
      {/* Header row — mode toggle on the right, preset entry on the left.
          Both stay visible at all times so neither path is hidden. */}
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
            Formula
          </span>
          {!presetsOpen && (
            <button
              onClick={() => { setPresetsOpen(true); setOpenPresetId(null); }}
              className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
              title="Start from a built-in template"
            >
              ✨ Start from preset
            </button>
          )}
        </div>
        {/* Chips ↔ Text mode toggle. Segmented control; the active mode
            is solid, the other is a hairline button. */}
        <div className="inline-flex items-center gap-0.5 rounded-md border border-gray-200 bg-gray-50 p-0.5 text-[11px] font-medium">
          {(['chips', 'text'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`rounded px-2 py-0.5 transition-colors ${
                mode === m
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title={m === 'chips' ? 'Click-to-assemble chip builder' : 'Type the formula as text'}
            >
              {m === 'chips' ? 'Chips' : 'Text'}
            </button>
          ))}
        </div>
      </div>

      {/* Preset banner — only when open. Two states:
          (1) idle: list every preset as a clickable card.
          (2) configuring: one preset is selected, show its slot pickers
              + an Apply button.
          Closing the banner only hides it; the user can re-open from
          the pill in the header. */}
      {presetsOpen && (
        <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-xs font-semibold text-amber-900">
                Start from a preset
              </div>
              <p className="text-[11px] text-amber-800">
                Pick a recipe, choose which sub-metric fills each slot, and apply. You can still tweak the result afterwards.
              </p>
            </div>
            <button
              onClick={() => { setPresetsOpen(false); setOpenPresetId(null); }}
              className="rounded p-0.5 text-amber-800 hover:bg-amber-100"
              aria-label="Dismiss presets"
              title="Build from scratch"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {noPresetable ? (
            <p className="rounded border border-amber-300 bg-white p-2 text-[11px] text-amber-900">
              Add at least one sub-metric above to apply a preset — presets reference your sub-metrics by code.
            </p>
          ) : openPresetId === null ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {FORMULA_PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => openPreset(p.id)}
                  className="flex flex-col items-start gap-1 rounded-md border border-amber-200 bg-white p-2 text-left hover:border-amber-300 hover:bg-amber-50"
                >
                  <span className="text-xs font-semibold text-gray-800">{p.label}</span>
                  <span className="text-[11px] text-gray-600">{p.description}</span>
                  <span className="text-[10px] uppercase tracking-wider text-amber-700">
                    {p.slots.length === 1 ? '1 slot' : `${p.slots.length} slots`}
                    {p.needsCohortGrouping ? ' · uses cohort grouping' : ''}
                  </span>
                </button>
              ))}
            </div>
          ) : (() => {
            const p = FORMULA_PRESETS.find((x) => x.id === openPresetId)!;
            const compiled = applyPreset(p, slotMap);
            return (
              <div className="rounded-md border border-amber-200 bg-white p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs font-semibold text-gray-800">{p.label}</div>
                    <p className="text-[11px] text-gray-600">{p.description}</p>
                  </div>
                  <button
                    onClick={() => setOpenPresetId(null)}
                    className="text-[11px] text-amber-800 underline hover:text-amber-900"
                  >
                    Back
                  </button>
                </div>
                {p.needsCohortGrouping && (
                  <p className="rounded border border-indigo-200 bg-indigo-50/70 p-1.5 text-[11px] text-indigo-800">
                    Set <strong>Cohort grouping</strong> above to <em>By AON bucket</em> (or another non-project grouping) so the cohort math has peer groups to compare against.
                  </p>
                )}
                <div className="space-y-1.5">
                  {p.slots.map((slot) => (
                    <div key={slot.placeholder} className="flex items-center gap-2">
                      <span className="w-28 shrink-0 text-[11px] font-medium text-gray-700">
                        {slot.label}
                      </span>
                      <select
                        value={slotMap[slot.placeholder] ?? ''}
                        onChange={(e) => setSlotMap((prev) => ({ ...prev, [slot.placeholder]: e.target.value }))}
                        className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
                      >
                        <option value="">— pick a sub-metric —</option>
                        {availableSubs.map((s) => (
                          <option key={s.code} value={s.code}>{s.name}</option>
                        ))}
                      </select>
                      {slot.hint && (
                        <span className="text-[10px] text-gray-500" title={slot.hint}>?</span>
                      )}
                    </div>
                  ))}
                </div>
                {compiled && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5">
                      Preview
                    </div>
                    <pre className="overflow-x-auto rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-[11px] text-gray-700 whitespace-pre-wrap">
{compiled}
                    </pre>
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    onClick={() => setOpenPresetId(null)}
                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={applyOpenPreset}
                    disabled={!compiled}
                    className="rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    Apply preset
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Text mode — single textarea, live validator error from the
          parent. Round-trips through tokenize() when the admin flips
          back to chips. */}
      {isText && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
            Formula (text)
          </div>
          <textarea
            value={formula}
            onChange={(e) => onChange(e.target.value)}
            rows={Math.min(10, Math.max(3, formula.split('\n').length))}
            placeholder="Examples:&#10;  achievement / target * 100&#10;  if(value >= cohort_median(value), 15, 0)"
            spellCheck={false}
            className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm font-mono leading-relaxed focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
          />
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          <p className="mt-1 text-[11px] text-gray-500">
            Type or paste a formula. Switch back to <strong>Chips</strong> to keep editing visually — the text round-trips with no loss.
          </p>
        </div>
      )}

      {/* Built formula — chips row */}
      {!isText && (
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
          Current formula
        </div>
        <div className="flex min-h-[2.5rem] flex-wrap items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50/60 p-2">
          {tokens.length === 0 ? (
            <span className="text-xs text-gray-400">Nothing yet — apply a preset above, or pick a sub-metric / number below.</span>
          ) : tokens.map((t, idx) => (
            <FormulaChip
              key={idx}
              token={t}
              nameByCode={nameByCode}
              onRemove={() => removeAt(idx)}
            />
          ))}
          {tokens.length > 0 && (
            <div className="ml-auto flex items-center gap-1">
              <button
                onClick={backspace}
                className="rounded-md p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                title="Remove last token"
                aria-label="Backspace"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={clear}
                className="rounded-md px-2 py-0.5 text-[11px] font-medium text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                title="Clear all"
              >
                Clear
              </button>
            </div>
          )}
        </div>
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
      )}

      {/* All chip-mode button rows are hidden in text mode — they don't
          serve someone who's typing the formula directly, and they make
          the card noisy. Wrapped together so chip-mode is one block. */}
      {!isText && (<>
      {/* Sub-metric picker — show the user-entered name. The token we
          push still carries the slug code so the parser stays happy. */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
          Sub-metrics
        </div>
        <div className="flex flex-wrap gap-1.5">
          {availableSubs.length === 0 ? (
            <span className="text-xs text-gray-400">Add a sub-metric above to use it here.</span>
          ) : availableSubs.map((s) => (
            <button
              key={s.code}
              onClick={() => append({ kind: 'ref', code: s.code })}
              className="rounded-md border border-primary-200 bg-primary-50 px-2.5 py-1 text-sm font-medium text-primary-700 hover:bg-primary-100"
              title={`code: ${s.code}`}
            >
              {s.name}
            </button>
          ))}
        </div>
      </div>

      {/* Operators + parens + number */}
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
            Operators
          </div>
          <div className="inline-flex items-center gap-1">
            {([
              ['+', '+'], ['-', '−'], ['*', '×'], ['/', '÷'],
            ] as const).map(([op, label]) => (
              <button
                key={op}
                onClick={() => append({ kind: 'op', op })}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
            Parens
          </div>
          <div className="inline-flex items-center gap-1">
            {(['(', ')'] as const).map((side) => (
              <button
                key={side}
                onClick={() => append({ kind: 'paren', side })}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm font-mono text-gray-700 hover:bg-gray-50"
              >
                {side}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
            Number
          </div>
          {/* Heights / paddings match the Operator + Paren buttons above
              (px-2.5 py-1 text-sm) so the three controls sit on the
              same baseline. */}
          <div className="inline-flex items-center gap-1">
            <input
              type="number"
              inputMode="decimal"
              value={numInput}
              onChange={(e) => setNumInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addNumber(); }}
              placeholder="e.g. 100"
              className="w-24 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
            />
            <button
              onClick={addNumber}
              disabled={!numInput || !Number.isFinite(Number(numInput))}
              className="rounded-md bg-primary-600 px-2.5 py-1 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Comparisons + logical keywords + conditional */}
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
            Compare
          </div>
          <div className="inline-flex items-center gap-1">
            {([
              ['>', '>'], ['>=', '≥'], ['<', '<'], ['<=', '≤'], ['==', '='], ['!=', '≠'],
            ] as const).map(([op, label]) => (
              <button
                key={op}
                onClick={() => append({ kind: 'cmp', op })}
                className="rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-sm font-medium text-blue-700 hover:bg-blue-100"
                title={`comparison: ${op}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
            Logic
          </div>
          <div className="inline-flex items-center gap-1">
            {(['and', 'or', 'not'] as const).map((op) => (
              <button
                key={op}
                onClick={() => append({ kind: 'logic', op })}
                className="rounded-md border border-purple-200 bg-purple-50 px-2.5 py-1 text-sm font-medium text-purple-700 hover:bg-purple-100"
              >
                {op}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
            Conditional
          </div>
          {/* Clicking `if` drops the whole call shell — `if ( , , )` — so
              the admin only fills the three placeholders. Saves four
              extra clicks compared to building it piece-by-piece. */}
          <div className="inline-flex items-center gap-1">
            <button
              onClick={() => setTokens([
                ...tokens,
                { kind: 'fn', name: 'if' },
                { kind: 'paren', side: '(' },
                { kind: 'comma' },
                { kind: 'comma' },
                { kind: 'paren', side: ')' },
              ])}
              className="rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1 text-sm font-medium text-teal-700 hover:bg-teal-100"
              title="Insert if(cond, then, else) template"
            >
              if(cond, then, else)
            </button>
            <button
              onClick={() => append({ kind: 'comma' })}
              className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm font-mono text-gray-700 hover:bg-gray-50"
              title="Argument separator"
            >
              ,
            </button>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
            Literal
          </div>
          <div className="inline-flex items-center gap-1">
            <button onClick={() => append({ kind: 'bool', value: true  })}
              className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-sm font-medium text-amber-700 hover:bg-amber-100">true</button>
            <button onClick={() => append({ kind: 'bool', value: false })}
              className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-sm font-medium text-amber-700 hover:bg-amber-100">false</button>
            <button onClick={() => append({ kind: 'null' })}
              className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-sm font-medium text-amber-700 hover:bg-amber-100">null</button>
          </div>
        </div>
      </div>

      {/* Cohort helpers + implicit per-employee context.
          The cohort_*() buttons need a sub-metric argument, so they
          render one button per (function × sub-metric) — clicking
          drops the complete `cohort_fn(value)` call as four tokens.
          The implicit-ref chips drop a single bare identifier that
          the server treats as the employee's own context value. */}
      <div className="space-y-2 rounded-md border border-gray-100 bg-gray-50/40 p-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
              Cohort aggregations
              <span className="ml-1 font-normal normal-case text-gray-400">
                (peer-group median / σ / count — defined by Cohort grouping above)
              </span>
            </div>
            {availableSubs.length === 0 ? (
              <span className="text-xs text-gray-400">Add a sub-metric to use a cohort aggregation.</span>
            ) : (
              <div className="space-y-1">
                {(['cohort_median', 'cohort_stddev', 'cohort_count'] as const).map((fn) => (
                  <div key={fn} className="flex flex-wrap items-center gap-1.5">
                    <span className="font-mono text-[11px] text-teal-700 mr-1">{fn}(…)</span>
                    {availableSubs.map((s) => (
                      <button
                        key={s.code}
                        onClick={() => setTokens([
                          ...tokens,
                          { kind: 'fn', name: fn },
                          { kind: 'paren', side: '(' },
                          { kind: 'ref', code: s.code },
                          { kind: 'paren', side: ')' },
                        ])}
                        className="rounded-md border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700 hover:bg-teal-100"
                        title={`${fn}(${s.code})`}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
              Per-employee context
              <span className="ml-1 font-normal normal-case text-gray-400">
                (implicit values the engine injects per employee)
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {(['aon_months', 'submitted_this_period', 'cohort_size'] as const).map((code) => (
                <button
                  key={code}
                  onClick={() => append({ kind: 'implicit', code })}
                  className="rounded-md border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                  title={
                    code === 'aon_months'            ? 'Months since the employee joined the network'
                    : code === 'submitted_this_period' ? 'true / false — did the employee submit during the score period'
                    :                                   'How many employees are in this employee’s cohort'
                  }
                >
                  {code}
                </button>
              ))}
            </div>
          </div>
        </div>

      <p className="text-[11px] text-gray-500">
        The assembled expression feeds the slab engine on the Configure tab.
        Examples: <code className="rounded bg-gray-100 px-1">achievement ÷ target × 100</code>;
        {' '}<code className="rounded bg-gray-100 px-1">if(value &gt;= cohort_median(value), 15, 0)</code>.
      </p>
      </>)}
    </div>
  );
}

interface FormulaChipProps {
  token: Token;
  /** code → display name lookup. For ref tokens we show the user-typed
   *  name when known so chips read "Target" instead of "target". The
   *  underlying serialised formula still uses the slug code. */
  nameByCode: Map<string, string>;
  onRemove: () => void;
}
function FormulaChip({ token, nameByCode, onRemove }: FormulaChipProps) {
  const labels: { bg: string; text: string; content: string } = (() => {
    switch (token.kind) {
      case 'ref': {
        const name = nameByCode.get(token.code.toLowerCase());
        // Red: no sub-metric has this code any more, and it is what is holding Save.
        return name
          ? { bg: 'bg-primary-100', text: 'text-primary-800', content: name }
          : { bg: 'bg-red-100', text: 'text-red-800', content: token.code };
      }
      case 'implicit': return { bg: 'bg-indigo-100',   text: 'text-indigo-800',   content: token.code };
      case 'num':      return { bg: 'bg-amber-100',    text: 'text-amber-800',    content: String(token.value) };
      case 'bool':     return { bg: 'bg-amber-100',    text: 'text-amber-800',    content: token.value ? 'true' : 'false' };
      case 'null':     return { bg: 'bg-amber-100',    text: 'text-amber-800',    content: 'null' };
      case 'op':       return { bg: 'bg-gray-200',     text: 'text-gray-800',     content: { '+': '+', '-': '−', '*': '×', '/': '÷' }[token.op] };
      case 'cmp':      return { bg: 'bg-blue-100',     text: 'text-blue-800',     content: { '>': '>', '>=': '≥', '<': '<', '<=': '≤', '==': '=', '!=': '≠' }[token.op] };
      case 'logic':    return { bg: 'bg-purple-100',   text: 'text-purple-800',   content: token.op };
      case 'paren':    return { bg: 'bg-gray-100',     text: 'text-gray-700',     content: token.side };
      case 'comma':    return { bg: 'bg-gray-100',     text: 'text-gray-700',     content: ',' };
      case 'fn':       return { bg: 'bg-teal-100',     text: 'text-teal-800',     content: token.name };
    }
  })();
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-sm font-medium ${labels.bg} ${labels.text}`}>
      <span className="font-mono">{labels.content}</span>
      <button
        onClick={onRemove}
        className="rounded p-0.5 opacity-60 hover:opacity-100 hover:bg-black/10"
        aria-label="Remove token"
        title="Remove"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Client-side formula validator
// ─────────────────────────────────────────────────────────────────────────────
//
// Mirrors the server-side parser in apps/api/src/utils/metricFormula.ts well
// enough to give the admin live feedback as they type. The server still
// re-validates on save — this is purely a UX assist.

// Identifiers the server parser treats as built-ins — admins may
// reference them without listing them as sub-metrics. Mirrors the
// RESERVED_IDENTIFIERS set in apps/api/src/utils/metricFormula.ts.
const CLIENT_RESERVED = new Set([
  'true', 'false', 'null',
  'and', 'or', 'not',
  'if',
  'cohort_median', 'cohort_stddev', 'cohort_count',
  'aon_months', 'submitted_this_period', 'cohort_size',
]);

function validateFormulaClient(src: string, knownCodes: string[]): void {
  const known = new Set(knownCodes.map((c) => c.toLowerCase()));
  let i = 0;
  const peek  = () => src[i] ?? '';
  const peek2 = () => src.slice(i, i + 2);
  const skipWs = () => { while (i < src.length && /\s/.test(src[i])) i++; };

  // ──────── grammar (low precedence → high) ────────
  //   or_expr   = and_expr ( 'or'  and_expr )*
  //   and_expr  = not_expr ( 'and' not_expr )*
  //   not_expr  = 'not' not_expr | eq_expr
  //   eq_expr   = cmp_expr ( ('==' | '!=') cmp_expr )?
  //   cmp_expr  = add_expr ( ('>' | '>=' | '<' | '<=') add_expr )?
  //   add_expr  = mul_expr ( ('+' | '-') mul_expr )*
  //   mul_expr  = unary    ( ('*' | '/') unary    )*
  //   unary     = ('+' | '-') unary | primary
  //   primary   = literal | call | identifier | '(' or_expr ')'

  const consumeKeyword = (word: string): boolean => {
    skipWs();
    const lower = src.slice(i, i + word.length).toLowerCase();
    if (lower !== word) return false;
    // Word boundary: next char must not be ident-continuing.
    const after = src[i + word.length] ?? '';
    if (/[a-zA-Z0-9_]/.test(after)) return false;
    i += word.length;
    return true;
  };

  const parseOr = (): void => {
    parseAnd();
    while (consumeKeyword('or')) parseAnd();
  };
  const parseAnd = (): void => {
    parseNot();
    while (consumeKeyword('and')) parseNot();
  };
  const parseNot = (): void => {
    if (consumeKeyword('not')) { parseNot(); return; }
    parseEq();
  };
  const parseEq = (): void => {
    parseCmp();
    skipWs();
    if (peek2() === '==' || peek2() === '!=') { i += 2; parseCmp(); }
  };
  const parseCmp = (): void => {
    parseAdd();
    skipWs();
    if (peek2() === '>=' || peek2() === '<=') { i += 2; parseAdd(); return; }
    if (peek()  === '>'  || peek()  === '<')  { i++;   parseAdd(); }
  };
  const parseAdd = (): void => {
    parseMul();
    skipWs();
    while (peek() === '+' || peek() === '-') { i++; parseMul(); skipWs(); }
  };
  const parseMul = (): void => {
    parseUnary();
    skipWs();
    while (peek() === '*' || peek() === '/') { i++; parseUnary(); skipWs(); }
  };
  const parseUnary = (): void => {
    skipWs();
    if (peek() === '+' || peek() === '-') { i++; parseUnary(); return; }
    parsePrimary();
  };
  const parsePrimary = (): void => {
    skipWs();
    if (peek() === '(') {
      i++; parseOr(); skipWs();
      if (peek() !== ')') throw new Error(`expected ')' at position ${i}`);
      i++;
      return;
    }
    if (/[0-9.]/.test(peek())) {
      const start = i;
      while (i < src.length && /[0-9.]/.test(src[i])) i++;
      const lit = src.slice(start, i);
      if ((lit.match(/\./g) ?? []).length > 1) throw new Error(`invalid number '${lit}'`);
      return;
    }
    if (/[a-zA-Z_]/.test(peek())) {
      const start = i;
      while (i < src.length && /[a-zA-Z0-9_]/.test(src[i])) i++;
      const code = src.slice(start, i).toLowerCase();

      // Literal keywords stand alone.
      if (code === 'true' || code === 'false' || code === 'null') return;

      skipWs();
      const isCall = peek() === '(';
      if (isCall) {
        i++; // open paren
        if (code === 'if') {
          // if(cond, then, else) — exactly three args
          parseOr(); expectComma('if'); parseOr(); expectComma('if'); parseOr();
        } else if (code === 'cohort_median' || code === 'cohort_stddev' || code === 'cohort_count') {
          // cohort_*(bare_ref) — argument must be a known sub-metric name
          skipWs();
          if (!/[a-zA-Z_]/.test(peek())) {
            throw new Error(`${code}(...) requires a sub-metric name as its argument`);
          }
          const argStart = i;
          while (i < src.length && /[a-zA-Z0-9_]/.test(src[i])) i++;
          const argCode = src.slice(argStart, i).toLowerCase();
          if (!known.has(argCode)) {
            throw new Error(`${code}() references unknown sub-metric: ${argCode}`);
          }
        } else {
          throw new Error(`unknown function: ${code}`);
        }
        skipWs();
        if (peek() !== ')') throw new Error(`expected ')' after ${code}(...) at position ${i}`);
        i++;
        return;
      }
      // Bare identifier — must be a known sub-metric or a reserved context value.
      if (!known.has(code) && !CLIENT_RESERVED.has(code)) {
        throw new Error(`unknown identifier: ${code}`);
      }
      return;
    }
    throw new Error(`unexpected character '${peek() || '<end>'}' at position ${i}`);
  };

  const expectComma = (where: string): void => {
    skipWs();
    if (peek() !== ',') throw new Error(`expected ',' in ${where}() at position ${i}`);
    i++;
  };

  parseOr();
  skipWs();
  if (i < src.length) throw new Error(`unexpected trailing input at position ${i}`);
}
